import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getUserInfo } from '../utils/tokenManager';
import { getCompanyBasicInfo, getCompanyUserInfo } from '../services/apiService';
import {
  fetchContractRequestList,
  requestContractCancel,
  updateContractCancelStatus,
  fetchContractRequestTimeline,
  createContractRequestComment,
  updateContractRequestStatus,
  proposeContractRequestCondition,
  signContractRequest
} from '../services/contractRequestService';
import { getSalesCondition } from '../services/salesConditionService';
import {
  createCampaignFromContractRequest,
  fetchCampaignByRequestId,
  fetchContentItemDetail,
  fetchContentItems,
  reviewContentItem,
  reviewContentItemUrl,
  upsertDeliverable
} from '../services/campaignContentService';
import {
  formatCompactNumber,
  formatPercent,
  getMockMetaInfluencerProfileByCompanyNo,
  MOCK_META_GRAPH_INFLUENCERS
} from '../mocks/metaGraphInfluencerMock';

const normalizeContentStatus = (status) => {
  const s = String(status || '').trim().toUpperCase();
  if (!s) return 'UNKNOWN';
  if (s === 'CREATED') return 'CREATED';
  if (s === 'DRAFT') return 'DRAFT';
  if (s === 'SUBMITTED') return 'SUBMITTED';
  if (s === 'APPROVED') return 'APPROVED';
  if (s === 'PUBLISHED') return 'PUBLISHED';
  if (s === 'REJECTED') return 'REJECTED';
  if (s === 'REVISION_REQUESTED') return 'REVISION_REQUESTED';
  return 'UNKNOWN';
};

const normalizeUrlStatus = (value) => {
  const v = String(value || '').trim().toUpperCase();
  if (v === 'NONE') return 'NONE';
  if (v === 'SUBMITTED') return 'SUBMITTED';
  if (v === 'APPROVED') return 'APPROVED';
  if (v === 'REJECTED') return 'REJECTED';
  return 'UNKNOWN';
};

const decodeHtmlEntities = (value) => {
  const text = String(value || '');
  if (!text || typeof document === 'undefined') return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value.replace(/\u00a0/g, ' ');
};

const ContentStatusBadge = ({ status }) => {
  const s = normalizeContentStatus(status);
  const badge = (() => {
    switch (s) {
      case 'CREATED':
      case 'DRAFT':
        return { label: '작성중', className: 'bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-500/10' };
      case 'SUBMITTED':
        return { label: '검토중', className: 'bg-yellow-50 text-yellow-800 ring-1 ring-inset ring-yellow-600/20' };
      case 'APPROVED':
        return { label: '승인됨', className: 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20' };
      case 'PUBLISHED':
        return { label: '게시됨', className: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-700/10' };
      case 'REJECTED':
      case 'REVISION_REQUESTED':
        return { label: '수정요청', className: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/10' };
      default:
        return { label: String(status || '-'), className: 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10' };
    }
  })();

  return (
    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${badge.className}`}>
      {badge.label}
    </span>
  );
};

const UrlStatusBadge = ({ status }) => {
  const s = normalizeUrlStatus(status);
  const badge = (() => {
    switch (s) {
      case 'NONE':
        return { label: 'URL 미제출', className: 'bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-500/10' };
      case 'SUBMITTED':
        return { label: 'URL 검토중', className: 'bg-yellow-50 text-yellow-800 ring-1 ring-inset ring-yellow-600/20' };
      case 'APPROVED':
        return { label: 'URL 승인', className: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-700/10' };
      case 'REJECTED':
        return { label: 'URL 반려', className: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/10' };
      default:
        return { label: String(status || '-'), className: 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10' };
    }
  })();

  return (
    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${badge.className}`}>
      {badge.label}
    </span>
  );
};

const ContractProgressSteps = [
  { key: 'REQUEST', label: '계약 신청' },
  { key: 'APPROVAL', label: '계약 승인' },
  { key: 'SIGNATURE', label: '계약서 작성' },
  { key: 'PLAN_WRITE', label: '콘텐츠 기획안 작성 대기' },
  { key: 'PLAN_APPROVAL', label: '콘텐츠 기획안 승인' },
  { key: 'CONTENT_REGISTER', label: '콘텐츠 등록 대기' },
  { key: 'CONTENT_APPROVAL', label: '콘텐츠 승인' },
  { key: 'CAMPAIGN_START', label: '캠페인 시작' },
  { key: 'CAMPAIGN_END', label: '캠페인 종료' }
];

const ContractProgressGroups = [
  { key: 'CONTRACT', label: '계약', stepKeys: ['REQUEST', 'APPROVAL', 'SIGNATURE'] },
  { key: 'PLAN', label: '콘텐츠 기획', stepKeys: ['PLAN_WRITE', 'PLAN_APPROVAL'] },
  { key: 'CONTENT', label: '콘텐츠 작성', stepKeys: ['CONTENT_REGISTER', 'CONTENT_APPROVAL'] },
  { key: 'CAMPAIGN', label: '캠페인', stepKeys: ['CAMPAIGN_START', 'CAMPAIGN_END'] }
];

const resolveProgressGroupKey = (stepKey) => {
  const k = String(stepKey || '').trim().toUpperCase();
  if (k === 'REQUEST' || k === 'APPROVAL' || k === 'SIGNATURE') return 'CONTRACT';
  if (k === 'PLAN_WRITE' || k === 'PLAN_APPROVAL') return 'PLAN';
  if (k === 'CONTENT_REGISTER' || k === 'CONTENT_APPROVAL') return 'CONTENT';
  if (k === 'CAMPAIGN_START' || k === 'CAMPAIGN_END') return 'CAMPAIGN';
  return 'CONTRACT';
};

const resolveGroupLabel = (groupKey) => {
  return ContractProgressGroups.find((g) => g.key === groupKey)?.label || '-';
};

const ContractProgressNavigator = ({
  currentStepKey,
  nextStepKey,
  helperText,
  selectedGroupKey,
  onSelectGroup,
  actionStepKey = null,
  actionButtonLabel = '해야할 일',
  onActionClick,
  topRightSlot = null,
  visibleStepKeys = null,
  currentStepTitle = '',
  stepLabelOverrides = null,
  tone = 'default'
}) => {
  const isDangerTone = String(tone || '').trim().toLowerCase() === 'danger';
  const visibleSteps = Array.isArray(visibleStepKeys) && visibleStepKeys.length > 0
    ? ContractProgressSteps.filter((step) => visibleStepKeys.includes(step.key)).map((step) => ({
      ...step,
      label:
        stepLabelOverrides && typeof stepLabelOverrides === 'object' && String(step.key || '') in stepLabelOverrides
          ? String(stepLabelOverrides[String(step.key || '')])
          : step.label
    }))
    : ContractProgressSteps;
  const visibleGroups = ContractProgressGroups.filter((g) => visibleSteps.some((s) => g.stepKeys.includes(s.key)));
  const currentGroupKey = resolveProgressGroupKey(currentStepKey);
  const currentIndex = visibleGroups.findIndex((g) => g.key === currentGroupKey);
  const currentStepLabel =
    visibleSteps.find((step) => step.key === currentStepKey)?.label ||
    ContractProgressSteps.find((step) => step.key === currentStepKey)?.label ||
    '-';
  const actionStepLabel = actionStepKey
    ? (
      visibleSteps.find((step) => step.key === actionStepKey)?.label ||
      ContractProgressSteps.find((step) => step.key === actionStepKey)?.label ||
      '-'
    )
    : null;
  const nextGroupKey = nextStepKey ? resolveProgressGroupKey(nextStepKey) : null;
  const nextStepDetailLabel = nextStepKey
    ? (visibleSteps.find((step) => step.key === nextStepKey)?.label || ContractProgressSteps.find((step) => step.key === nextStepKey)?.label || '-')
    : '';
  const nextStepLabel = nextStepKey
    ? (
      nextGroupKey && nextGroupKey !== currentGroupKey
        ? `${resolveGroupLabel(nextGroupKey)} (${nextStepDetailLabel})`
        : nextStepDetailLabel
    )
    : '';
  const effectiveDetailTitle = String(currentStepTitle || '').trim() || String(currentStepLabel || '').trim();
  const groupTitle = resolveGroupLabel(currentGroupKey);
  const currentTitle = groupTitle && groupTitle !== '-' && effectiveDetailTitle && effectiveDetailTitle !== groupTitle
    ? `${groupTitle} (${effectiveDetailTitle})`
    : (effectiveDetailTitle || groupTitle || '-');
  const headingClass = isDangerTone ? 'text-red-700' : 'text-indigo-700';
  const currentTitleClass = isDangerTone ? 'text-red-900' : 'text-slate-900';
  const actionButtonClass = isDangerTone
    ? 'border-red-200 bg-white/90 text-red-700 hover:bg-red-50'
    : 'border-indigo-200 bg-white/90 text-indigo-700 hover:bg-white';
  const actionDividerClass = isDangerTone ? 'text-red-200' : 'text-indigo-200';
  const nextCardClass = isDangerTone
    ? 'bg-white/80 text-red-700 border border-red-100'
    : 'bg-white/80 text-slate-700 border border-indigo-100';
  const nextCardTitleClass = isDangerTone ? 'text-red-900' : 'text-slate-900';
  const helperTextClass = isDangerTone ? 'text-red-700' : 'text-slate-600';
  const stepHoverClass = isDangerTone ? 'hover:bg-red-50/60' : 'hover:bg-white/60';
  const actionGroupKey = actionStepKey ? resolveProgressGroupKey(actionStepKey) : null;

  return (
    <section className="py-1">
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className={`text-xs font-semibold tracking-wide ${headingClass}`}>계약 진행 현황</div>
            <div className={`mt-1 text-lg font-bold ${currentTitleClass}`}>
              <span className="ml-0">{currentTitle}</span>
            </div>
            {!actionStepKey && nextStepLabel ? (
              <div className={`mt-2 rounded-lg px-3 py-2 text-sm ${nextCardClass}`}>
                <span className={`font-semibold ${nextCardTitleClass}`}>다음 단계</span>
                <span className="mx-2 text-slate-300">|</span>
                <span>{nextStepLabel}</span>
              </div>
            ) : null}
          </div>
          {topRightSlot ? <div className="shrink-0">{topRightSlot}</div> : null}
        </div>

        <div className={`mt-4 text-sm ${helperTextClass}`}>{helperText}</div>

        <div className="mt-5 overflow-x-auto pb-1 -mx-1 px-1">
          <div className="min-w-[520px] flex items-start gap-0">
            {visibleGroups.map((group, idx) => {
              const isCompleted = currentIndex > idx;
              const isCurrent = currentIndex === idx;
              const isSelected = selectedGroupKey === group.key;
              const dotClass = isCompleted
                ? 'border-emerald-500 bg-emerald-500 text-white'
                : isCurrent
                  ? (isDangerTone ? 'border-red-600 bg-red-600 text-white ring-4 ring-red-100' : 'border-indigo-600 bg-indigo-600 text-white ring-4 ring-indigo-100')
                  : isSelected
                    ? (isDangerTone ? 'border-red-300 bg-red-50 text-red-700' : 'border-indigo-300 bg-indigo-50 text-indigo-700')
                    : 'border-gray-300 bg-white text-gray-400';
              const labelClass = isCurrent
                ? (isDangerTone ? 'text-red-700 font-bold' : 'text-indigo-700 font-bold')
                : isSelected
                  ? (isDangerTone ? 'text-red-600 font-semibold' : 'text-indigo-600 font-semibold')
                  : isCompleted
                    ? 'text-emerald-700 font-semibold'
                    : 'text-gray-500';
              const lineClass = currentIndex > idx ? 'bg-emerald-400' : 'bg-gray-200';

              return (
                <React.Fragment key={group.key}>
                  <div className="w-40 shrink-0">
                    <button
                      type="button"
                      className={`w-full rounded-xl px-1 py-1.5 transition-colors ${stepHoverClass}`}
                      onClick={() => onSelectGroup(group.key)}
                    >
                      <div className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full border text-xs font-bold ${dotClass}`}>
                        {isCompleted ? '✔' : idx + 1}
                      </div>
                      <div className={`mt-2 text-center text-[11px] leading-4 break-keep ${labelClass}`}>{group.label}</div>
                    </button>
                    {actionStepKey && isCurrent && actionGroupKey === group.key ? (
                      <div className="mt-2 flex justify-center">
                        <button
                          type="button"
                          className={`inline-flex w-full flex-col items-center justify-center rounded-lg border px-4 py-2 text-xs font-semibold shadow-sm transition-all hover:shadow-md ${actionButtonClass}`}
                          onClick={() => {
                            if (onActionClick) {
                              onActionClick();
                              return;
                            }
                            onSelectGroup(resolveProgressGroupKey(actionStepKey));
                          }}
                        >
                          <span className="whitespace-nowrap">{actionButtonLabel}</span>
                          {actionStepLabel ? <span className="mt-0.5 whitespace-nowrap text-slate-700">{actionStepLabel}</span> : null}
                        </button>
                      </div>
                    ) : null}
                  </div>
                  {idx !== visibleGroups.length - 1 && (
                    <div className="mt-4 h-0.5 flex-1 min-w-[28px] rounded-full bg-gray-200">
                      <div className={`h-full rounded-full ${lineClass}`}></div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const resolveContentCardAccentClass = (contentStatus, urlStatus) => {
  const s = normalizeContentStatus(contentStatus);
  const u = normalizeUrlStatus(urlStatus);
  if (u === 'APPROVED') return 'border-t-4 border-t-blue-500';
  if (s === 'APPROVED') return 'border-t-4 border-t-emerald-500';
  if (s === 'SUBMITTED') return 'border-t-4 border-t-amber-400';
  if (s === 'REJECTED' || s === 'REVISION_REQUESTED') return 'border-t-4 border-t-red-400';
  return 'border-t-4 border-t-gray-200';
};

const SectionCard = ({ title, children, className, bodyClassName, action, style }) => {
  return (
    <section
      className={`rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden flex flex-col ${className || ''}`}
      style={style}
    >
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
        <h3 className="text-base font-bold text-gray-900">{title}</h3>
        {action && <div>{action}</div>}
      </div>
      <div className={`px-5 py-5 flex-1 min-h-0 ${bodyClassName || ''}`}>{children}</div>
    </section>
  );
};

const ConditionFieldRow = ({ label, value }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-2 py-3 border-b border-gray-50 last:border-0 group">
      <div className="text-sm text-gray-500 font-medium flex items-center">
        {label}
      </div>
      <div className="text-sm text-gray-900 font-medium whitespace-pre-wrap break-words">{value}</div>
    </div>
  );
};

const getText = (value) => {
  const v = String(value ?? '').trim();
  return v.length > 0 ? v : '-';
};

const getComparableText = (value) => {
  const v = String(value ?? '').trim();
  return v.length > 0 ? v : '-';
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

const sanitizeHtml = (html) => {
  try {
    let out = String(html || '');
    out = out.replace(/<\s*(script|style)([\s\S]*?)>([\s\S]*?)<\s*\/\s*\1\s*>/gi, '');
    out = out.replace(/\s+on[a-zA-Z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
    out = out.replace(/(href|src)\s*=\s*(["'])\s*(javascript:|data:text\/html)/gi, '$1=$2about:blank');
    out = out.replace(/(href|src)\s*=\s*(javascript:|data:text\/html)/gi, '$1=about:blank');
    return out;
  } catch {
    return '';
  }
};

const HtmlPreview = ({ html }) => {
  const safe = sanitizeHtml(html);
  return (
    <div
      className="text-[13px] leading-6 text-gray-900 break-words [&_img]:max-w-full [&_img]:h-auto [&_video]:max-w-full [&_video]:h-auto [&_iframe]:max-w-full [&_table]:w-full"
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
};

const Modal = ({ open, title, children, onClose, maxWidthClassName, maxHeightClassName, bodyClassName, zIndexClassName }) => {
  if (!open) return null;
  return (
    <div className={`fixed inset-0 ${zIndexClassName || 'z-50'} flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 transition-opacity`}>
      <div
        className={`w-full ${maxWidthClassName || 'max-w-lg'} rounded-xl bg-white shadow-2xl flex flex-col ${
          maxHeightClassName || 'max-h-[85vh]'
        } transform transition-all`}
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="text-base font-bold text-gray-900">{title}</div>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
            onClick={onClose}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={`px-5 py-5 flex-1 min-h-0 ${bodyClassName || 'overflow-auto'}`}>{children}</div>
      </div>
    </div>
  );
};

const CONDITION_HISTORY_FIELD_DEFS = [
  { key: 'sellStartDate', label: '판매 가능 시작일' },
  { key: 'sellEndDate', label: '판매 가능 종료일' },

  
  { key: 'deliverableSubmitDeadline', label: '필수 작업물 제출 기한' },
  { key: 'commissionPolicy', label: '판매 수수료 방식' },
  { key: 'contentCreationSupport', label: '콘텐츠 제작 지원' }
];

const STATUS_LABEL = {
  REQUESTED: '신청',
  APPROVED: '승인',
  REJECTED: '반려'
};

const CANCEL_LABEL = {
  NONE: '-',
  REQUESTED: '취소 요청됨',
  APPROVED: '취소 확정',
  REJECTED: '취소 거절',
  WITHDRAWN: '요청 철회'
};

const DELIVERABLE_OPTIONS = [
  { value: 'INSTAGRAM_FEED', label: '인스타그램 피드' },
  { value: 'INSTAGRAM_REELS', label: '인스타그램 릴스' },
  { value: 'YOUTUBE_VIDEO', label: '유튜브 영상(롱폼)' },
  { value: 'YOUTUBE_SHORTS', label: '유튜브 쇼츠' },
  { value: 'X_CONTENT', label: '엑스 콘텐츠' },
  { value: 'TIKTOK_CONTENT', label: '틱톡 콘텐츠' },
  { value: 'NAVER_BLOG', label: '네이버 블로그' },
  { value: 'BLOG', label: '블로그' }
];

const resolveDeliverableLabel = (type) => {
  const key = String(type || '').trim();
  if (!key) return '';
  const match = DELIVERABLE_OPTIONS.find((opt) => String(opt.value) === key);
  return match ? match.label : key;
};

const CHANNEL_OPTIONS = [
  { value: 'INSTAGRAM', label: 'INSTAGRAM' },
  { value: 'YOUTUBE', label: 'YOUTUBE' },
  { value: 'BLOG', label: 'BLOG' },
  { value: 'X', label: 'X' },
  { value: 'TIKTOK', label: 'TIKTOK' },
  { value: 'NAVER', label: 'NAVER' }
];

const DELIVERABLE_CHANNEL_MAP = {
  INSTAGRAM_REELS: 'INSTAGRAM',
  INSTAGRAM_FEED: 'INSTAGRAM',
  YOUTUBE_SHORTS: 'YOUTUBE',
  YOUTUBE_VIDEO: 'YOUTUBE',
  BLOG: 'BLOG',
  NAVER_BLOG: 'NAVER',
  X_CONTENT: 'X',
  TIKTOK_CONTENT: 'TIKTOK'
};

const getDeliverableOptionsByChannel = (channel) => {
  const key = String(channel || '').trim().toUpperCase();
  return DELIVERABLE_OPTIONS.filter((opt) => DELIVERABLE_CHANNEL_MAP[String(opt.value || '')] === key);
};

const extractDeliverableTemplate = (selected) => {
  try {
    const req = selected?.requestedSalesCondition && typeof selected.requestedSalesCondition === 'object' ? selected.requestedSalesCondition : {};
    const candidateA = req?.deliverableTemplate;
    if (candidateA && typeof candidateA === 'object') return candidateA;
    const candidateB = req?.deliverable_template;
    if (candidateB && typeof candidateB === 'object') return candidateB;
    const candidateC = req?.필수작업물템플릿;
    if (candidateC && typeof candidateC === 'object') return candidateC;
    return {};
  } catch {
    return {};
  }
};

const resolveChannelFromAnyKey = (raw) => {
  const key = String(raw || '').trim().toUpperCase();
  if (!key) return '';
  if (['INSTAGRAM', 'YOUTUBE', 'BLOG', 'X', 'TIKTOK', 'NAVER'].includes(key)) return key;
  const mapped = DELIVERABLE_CHANNEL_MAP?.[key];
 
  if (key.startsWith('INSTAGRAM')) return 'INSTAGRAM';
  if (key.startsWith('YOUTUBE')) return 'YOUTUBE';
  if (key.startsWith('BLOG')) return 'BLOG';
  if (key.startsWith('NAVER')) return 'NAVER';
  if (key.startsWith('X')) return 'X';
  if (key.startsWith('TIKTOK')) return 'TIKTOK';
  return '';
};

const getChannelIconBadge = (channelRaw) => {
  const channel = resolveChannelFromAnyKey(channelRaw);
  const baseClassName = 'inline-flex items-center justify-center shrink-0';

  const IconWrap = ({ children, label }) => (
    <span
      className={`${baseClassName} h-6 w-6 rounded-[6px] overflow-hidden`}
      role="img"
      aria-label={label}
    >
      {children}
    </span>
  );

  const InstagramIcon = () => (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
      <defs>
        <linearGradient id="ig-g" x1="0" y1="24" x2="24" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FEDA75" />
          <stop offset="0.35" stopColor="#FA7E1E" />
          <stop offset="0.65" stopColor="#D62976" />
          <stop offset="1" stopColor="#4F5BD5" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="22" height="22" rx="6" fill="url(#ig-g)" />
      <rect x="6.5" y="6.5" width="11" height="11" rx="3.2" fill="none" stroke="white" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="3" fill="none" stroke="white" strokeWidth="1.8" />
      <circle cx="16.9" cy="7.3" r="1" fill="white" />
    </svg>
  );

  const YouTubeIcon = () => (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
      <rect x="1" y="1" width="22" height="22" rx="6" fill="#FFFFFF" />
      <rect x="4.2" y="7.2" width="15.6" height="9.6" rx="3" fill="#FF0000" />
      <path d="M11 9.2v5.6l5-2.8-5-2.8Z" fill="white" />
    </svg>
  );

  const XIcon = () => (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
      <rect x="1" y="1" width="22" height="22" rx="6" fill="#111827" />
      <path
        d="M15.8 6H18l-5.2 6.2L18.8 18H14.8l-3.1-3.9L8.4 18H6.2l5.6-6.7L5.2 6h4.1l2.8 3.5L15.8 6Z"
        fill="#ffffff"
      />
    </svg>
  );

  const TikTokIcon = () => (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
      <rect x="1" y="1" width="22" height="22" rx="6" fill="#0B0B0F" />
      <path
        d="M14.2 6.3c.8 1.1 1.8 1.7 3.2 1.8v2.1c-1.2 0-2.3-.3-3.2-.9v5.6c0 2.1-1.7 3.8-3.8 3.8s-3.8-1.7-3.8-3.8 1.7-3.8 3.8-3.8c.3 0 .7 0 1 .1v2.2c-.3-.1-.6-.2-1-.2-1 0-1.8.8-1.8 1.8S9.4 17 10.4 17s1.8-.8 1.8-1.8V5.8h2v.5Z"
        fill="#ffffff"
        opacity="0.92"
      />
      <path
        d="M12.4 5.8v9.4c0 1-.8 1.8-1.8 1.8-.5 0-.9-.2-1.2-.5.3.7 1 1.2 2 1.2 1.2 0 2.2-1 2.2-2.2V7.7c.9.6 2 1 3.2 1V8.1c-1.4-.1-2.4-.8-3.2-1.8v-.5h-1.2Z"
        fill="#25F4EE"
        opacity="0.9"
      />
      <path
        d="M11.9 6.1v9.3c0 1-.8 1.8-1.8 1.8-.8 0-1.5-.5-1.7-1.2.1.9.9 1.6 1.9 1.6 1.2 0 2.2-1 2.2-2.2V7.9c.9.6 2 1 3.2 1V8.3c-1.4-.1-2.4-.8-3.2-1.8V6.1h-.6Z"
        fill="#FE2C55"
        opacity="0.85"
      />
    </svg>
  );

  const NaverIcon = () => (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
      <rect x="1" y="1" width="22" height="22" rx="6" fill="#03C75A" />
      <path d="M7.6 17.6V6.4h2.4l4 7V6.4h2.4v11.2H14l-4-7v7H7.6Z" fill="white" />
    </svg>
  );

  const BlogIcon = () => (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
      <rect x="1" y="1" width="22" height="22" rx="6" fill="#F59E0B" />
      <path
        d="M8 7.5h6.1c2.1 0 3.4 1.2 3.4 3 0 1.1-.6 2-1.6 2.5 1.3.4 2.1 1.4 2.1 2.8 0 2-1.5 3.2-3.9 3.2H8V7.5Zm2.6 4.5h3.2c.7 0 1.2-.4 1.2-1s-.5-1-1.2-1h-3.2v2Zm0 6h3.6c.9 0 1.4-.4 1.4-1.1s-.5-1.1-1.4-1.1h-3.6V18Z"
        fill="white"
        opacity="0.95"
      />
    </svg>
  );

  switch (channel) {
    case 'INSTAGRAM':
      return (
        <IconWrap label="인스타그램">
          <InstagramIcon />
        </IconWrap>
      );
    case 'YOUTUBE':
      return (
        <IconWrap label="유튜브">
          <YouTubeIcon />
        </IconWrap>
      );
    case 'X':
      return (
        <IconWrap label="엑스">
          <XIcon />
        </IconWrap>
      );
    case 'TIKTOK':
      return (
        <IconWrap label="틱톡">
          <TikTokIcon />
        </IconWrap>
      );
    case 'NAVER':
      return (
        <IconWrap label="네이버">
          <NaverIcon />
        </IconWrap>
      );
    case 'BLOG':
      return (
        <IconWrap label="블로그">
          <BlogIcon />
        </IconWrap>
      );
    default:
      return (
        <span className={`${baseClassName} h-6 w-6 rounded-[6px] bg-gray-100 text-gray-400 text-[10px] font-bold`} aria-hidden="true">
          -
        </span>
      );
  }
};

const findTemplateConfigForChannel = (deliverableTemplate, channel) => {
  if (!deliverableTemplate || typeof deliverableTemplate !== 'object') return null;
  const normalized = String(channel || '').trim().toUpperCase();
  if (!normalized) return null;
  const direct = deliverableTemplate?.[normalized];
  if (direct && typeof direct === 'object') return direct;
  const key = Object.keys(deliverableTemplate).find((k) => String(k || '').trim().toUpperCase() === normalized);
  if (!key) return null;
  const cfg = deliverableTemplate?.[key];
  if (cfg && typeof cfg === 'object') return cfg;

  try {
    const entries = Object.entries(deliverableTemplate);
    for (const [k, v] of entries) {
      if (!v || typeof v !== 'object') continue;
      const chByKey = resolveChannelFromAnyKey(k);
      const chByType = resolveChannelFromAnyKey(v?.type);
      const resolved = chByKey || chByType;
      if (resolved && resolved === normalized) {
        return v;
      }
    }
  } catch {}

  return null;
};

const getTemplateConfigForChannel = (selected, channel) => {
  try {
    const tmpl = extractDeliverableTemplate(selected);
    const cfg = findTemplateConfigForChannel(tmpl, channel);
    if (!cfg) return null;
    const type = String(cfg?.type || '').trim();
    const requiredCount = Number(String((cfg?.requiredCount ?? cfg?.required_count) ?? '').trim() || 0);
    return {
      type,
      requiredCount: Number.isFinite(requiredCount) ? requiredCount : 0
    };
  } catch {
    return null;
  }
};

const getTemplateChannels = (selected) => {
  try {
    const tmpl = extractDeliverableTemplate(selected);
    const channels = ['INSTAGRAM', 'YOUTUBE', 'BLOG'];
    const base = channels.filter((ch) => {
      const cfg = findTemplateConfigForChannel(tmpl, ch);
      const type = String(cfg?.type || '').trim();
      const requiredCount = Number(String((cfg?.requiredCount ?? cfg?.required_count) ?? '').trim() || 0);
      return !!type && Number.isFinite(requiredCount) && requiredCount > 0;
    });

    if (base.length > 0) return base;

    const derived = new Set();
    const entries = tmpl && typeof tmpl === 'object' ? Object.entries(tmpl) : [];
    for (const [k, rawCfg] of entries) {
      const cfg = rawCfg && typeof rawCfg === 'object' ? rawCfg : {};
      const requiredCount = Number(String((cfg?.requiredCount ?? cfg?.required_count) ?? '').trim() || 0);
      const type = String(cfg?.type || '').trim();
      if (!type || !Number.isFinite(requiredCount) || requiredCount <= 0) continue;
      const ch = resolveChannelFromAnyKey(k) || resolveChannelFromAnyKey(type);
      if (ch) derived.add(ch);
    }
    return Array.from(derived);
  } catch {
    return [];
  }
};

const getFixedTemplateChannel = (selected) => {
  try {
    const req = selected?.requestedSalesCondition && typeof selected.requestedSalesCondition === 'object' ? selected.requestedSalesCondition : {};
    const raw = req?.templateChannel ?? req?.channel ?? selected?.templateChannel ?? selected?.channel;
    return resolveChannelFromAnyKey(raw);
  } catch {
    return '';
  }
};

const getApprovalChannels = (selected) => {
  const templateChannels = getTemplateChannels(selected);
  if (Array.isArray(templateChannels) && templateChannels.length > 0) return templateChannels;
  const fixed = getFixedTemplateChannel(selected);
  if (fixed) return [fixed];
  return [];
};

const ALLOW_SELF_CANCEL_DECISION = String(process.env.REACT_APP_ALLOW_SELF_CANCEL_DECISION || '').toLowerCase() === 'true';

const toLocalText = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ko-KR');
};

const formatTimelineDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}`;
};

function resolveActorLabel(side) {
  const s = String(side || '').trim().toUpperCase();
  if (s === 'CRS') return '인플루언서';
  if (s === 'TRP') return '공급사';
  return '-';
}

const resolveTimelineLabel = (scope, action) => {
  const sc = String(scope || '').trim().toUpperCase();
  const ac = String(action || '').trim().toUpperCase();
  if (sc === 'SIGNATURE') {
    if (ac === 'SIGN') return '계약서 서명';
    return '서명';
  }
  if (sc === 'CONDITION') {
    if (ac === 'PROPOSE') return '계약조건 제안';
    if (ac === 'APPROVE') return '계약조건 승인';
    if (ac === 'REJECT') return '계약조건 반려';
    return '계약조건';
  }
  if (sc === 'REQUEST') {
    if (ac === 'REQUESTED') return '계약 신청';
    if (ac === 'REVIEW') return '접수';
    if (ac === 'APPROVE') return '승인';
    if (ac === 'REJECT') return '반려';
    return '계약';
  }
  if (sc === 'CONTENT') {
    if (ac === 'REVISION_CREATED') return '콘텐츠 기획안 수정등록';
    if (ac === 'SUBMIT') return '콘텐츠 기획안 제출';
    if (ac === 'APPROVE') return '콘텐츠 기획안 승인';
    if (ac === 'REQUEST_REVISION') return '콘텐츠 기획안 수정요청';
    return '콘텐츠 기획안';
  }
  if (sc === 'URL') {
    if (ac === 'SUBMIT') return 'URL 제출';
    if (ac === 'APPROVE') return 'URL 승인';
    if (ac === 'REQUEST_REVISION') return 'URL 반려';
    return 'URL';
  }
  if (sc === 'CANCEL') {
    if (ac === 'REQUEST') return '취소 요청';
    if (ac === 'APPROVE') return '취소 승인';
    if (ac === 'REJECT') return '취소 거절';
    if (ac === 'WITHDRAW') return '취소 철회';
    return '취소';
  }
  return sc || '이력';
};

const toEmbedUrl = (input) => {
  const raw = String(input || '').trim();
  if (!raw) return '';
  try {
    const u = new URL(raw);
    const host = String(u.hostname || '').replace(/^www\./i, '').toLowerCase();
    const path = String(u.pathname || '').replace(/\/+$/, '');

    if (host === 'instagram.com') {
      const m = path.match(/^\/(p|reel|tv)\/([^/]+)$/i);
      if (m) {
        const kind = String(m[1] || '').toLowerCase();
        const code = String(m[2] || '');
        return `https://www.instagram.com/${kind}/${code}/embed`;
      }
      return `https://www.instagram.com${path}/embed`;
    }

    if (host === 'youtu.be') {
      const id = path.replace(/^\//, '');
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const shorts = path.match(/^\/shorts\/([^/]+)$/i);
      if (shorts) return `https://www.youtube.com/embed/${shorts[1]}`;
      const id = u.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
  } catch {
    // ignore
  }
  return raw;
};

const ContractDetailPage = () => {
  const navigate = useNavigate();
  const { requestId: routeRequestId } = useParams();
  const requestId = Number(routeRequestId);

  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [activeTab, setActiveTab] = useState('CONDITION');

  const [campaignState, setCampaignState] = useState({ loading: false, error: '', data: null });
  const [deliverableType, setDeliverableType] = useState('INSTAGRAM_REELS');
  const [deliverableRequiredCount, setDeliverableRequiredCount] = useState('1');
  const [approveChannels, setApproveChannels] = useState([]);
  const [approveDeliverables, setApproveDeliverables] = useState({});

  const [contentRows, setContentRows] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState('');
  const [contentDetailOpen, setContentDetailOpen] = useState(false);
  const [contentDetailLoading, setContentDetailLoading] = useState(false);
  const [contentDetail, setContentDetail] = useState(null);
  const [contentReviewNote, setContentReviewNote] = useState('');
  const [urlReviewNote, setUrlReviewNote] = useState('');
  const [urlRejectAlsoRequestCopyRevision, setUrlRejectAlsoRequestCopyRevision] = useState(false);
  const [contentShowPlan, setContentShowPlan] = useState(true);

  const [contentRevisionRequestModalOpen, setContentRevisionRequestModalOpen] = useState(false);
  const [contentRevisionRequestNoteDraft, setContentRevisionRequestNoteDraft] = useState('');

  const [savingAction, setSavingAction] = useState('');
  const [cancelReviewNote, setCancelReviewNote] = useState('');

  const [decisionChoiceModalOpen, setDecisionChoiceModalOpen] = useState(false);
  const [decisionProposalCheckOpen, setDecisionProposalCheckOpen] = useState(false);
  const [decisionProposalCheckConfirmed, setDecisionProposalCheckConfirmed] = useState(false);
  const [decisionModalOpen, setDecisionModalOpen] = useState(false);
  const [decisionAction, setDecisionAction] = useState('');
  const [decisionReasonDraft, setDecisionReasonDraft] = useState('');

  const [conditionModalOpen, setConditionModalOpen] = useState(false);
  const [conditionMessage, setConditionMessage] = useState('');
  const [conditionSellStartDate, setConditionSellStartDate] = useState('');
  const [conditionSellEndDate, setConditionSellEndDate] = useState('');
  const [conditionDeliverableSubmitDeadline, setConditionDeliverableSubmitDeadline] = useState('');
  const [conditionCommissionPolicy, setConditionCommissionPolicy] = useState('');
  const [conditionContentCreationSupport, setConditionContentCreationSupport] = useState('');
  const [conditionSaving, setConditionSaving] = useState(false);
  const [conditionError, setConditionError] = useState('');

  const [conditionHistoryModalOpen, setConditionHistoryModalOpen] = useState(false);
  const [conditionHistoryTitle, setConditionHistoryTitle] = useState('');
  const [conditionHistoryCreatedAt, setConditionHistoryCreatedAt] = useState(null);
  const [conditionHistoryActorSide, setConditionHistoryActorSide] = useState('');
  const [conditionHistorySnapshot, setConditionHistorySnapshot] = useState(null);
  const [conditionHistoryDiff, setConditionHistoryDiff] = useState([]);

  const [isNegotiable, setIsNegotiable] = useState(false);

  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const [timelineState, setTimelineState] = useState({ loading: false, error: '', items: [] });
  const [contractTimelineDraft, setContractTimelineDraft] = useState('');
  const [contentTimelineDraft, setContentTimelineDraft] = useState('');
  const [timelineSaving, setTimelineSaving] = useState(false);

  const [timelineCommentModalOpen, setTimelineCommentModalOpen] = useState(false);
  const [timelineCommentModalTitle, setTimelineCommentModalTitle] = useState('');
  const [timelineCommentModalMessage, setTimelineCommentModalMessage] = useState('');
  const [timelineCommentModalCreatedAt, setTimelineCommentModalCreatedAt] = useState(null);
  const [timelineCommentModalActorSide, setTimelineCommentModalActorSide] = useState('');

  const contractDocRef = useRef(null);
  const [contractDocOpen, setContractDocOpen] = useState(false);
  const [contractAgreeChecked, setContractAgreeChecked] = useState(false);
  const [contractDocReadOnly, setContractDocReadOnly] = useState(false);
  const [contractSupplierInfo, setContractSupplierInfo] = useState(null);
  const [contractInfluencerInfo, setContractInfluencerInfo] = useState(null);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSaving, setCancelSaving] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const [timelineTableOpen, setTimelineTableOpen] = useState(false);

  const [contentPlanSelectionModalOpen, setContentPlanSelectionModalOpen] = useState(false);

  const loadSelected = async () => {
    if (!Number.isFinite(requestId) || requestId <= 0) {
      setSelected(null);
      setError('잘못된 요청번호입니다.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const list = await fetchContractRequestList({});
      const match = list.find((x) => x.requestId === requestId) || null;
      if (!match) {
        setSelected(null);
        setError('계약 요청 정보를 찾을 수 없습니다.');
        return;
      }
      setSelected(match);
      setCancelReviewNote(match.cancelReviewNote || '');
      setDecisionModalOpen(false);
      setDecisionAction('');
      setDecisionReasonDraft('');
      setAlertModalOpen(false);
      setAlertMessage('');
    } catch (e) {
      setSelected(null);
      setError(e?.message || '계약 요청 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const openContractDocPreview = () => {
    setContractDocReadOnly(true);
    setContractAgreeChecked(false);
    setContractDocOpen(true);
  };

  const loadTimeline = async (rid) => {
    if (!rid || !Number.isFinite(Number(rid))) return;
    try {
      setTimelineState({ loading: true, error: '', items: [] });
      const data = await fetchContractRequestTimeline({ requestId: rid, limit: 300 });
      const items = Array.isArray(data?.items) ? data.items : [];
      setTimelineState({ loading: false, error: '', items });
    } catch (e) {
      setTimelineState({ loading: false, error: e?.message || '타임라인을 불러오지 못했습니다.', items: [] });
    }
  };

  React.useEffect(() => {
    void loadSelected();
  }, [routeRequestId]);

  const loadCampaign = async (rid) => {
    if (!rid || !Number.isFinite(Number(rid))) return;
    try {
      setCampaignState({ loading: true, error: '', data: null });
      const data = await fetchCampaignByRequestId(rid);
      setCampaignState({ loading: false, error: '', data });
    } catch (e) {
      setCampaignState({ loading: false, error: e?.message || '캠페인 정보를 불러오지 못했습니다.', data: null });
    }
  };

  const loadContentItems = async (campaignId) => {
    if (!campaignId || !Number.isFinite(Number(campaignId))) return;
    try {
      setContentLoading(true);
      setContentError('');
      const items = await fetchContentItems(campaignId);
      setContentRows(items);
    } catch (e) {
      setContentError(e?.message || '콘텐츠 목록을 불러오지 못했습니다.');
      setContentRows([]);
    } finally {
      setContentLoading(false);
    }
  };

  React.useEffect(() => {
    const rid = selected?.requestId;
    if (!rid) {
      setCampaignState({ loading: false, error: '', data: null });
      setContentRows([]);
      setContentError('');
      setContentLoading(false);
      setTimelineState({ loading: false, error: '', items: [] });
      return;
    }
    void loadCampaign(rid);
    void loadTimeline(rid);
  }, [selected?.requestId]);

  React.useEffect(() => {
    const campaigns = Array.isArray(campaignState?.data?.campaigns)
      ? campaignState.data.campaigns
      : campaignState?.data?.campaign
        ? [campaignState.data.campaign]
        : [];
    if (campaigns.length === 0) {
      setContentRows([]);
      return;
    }
    void (async () => {
      try {
        setContentLoading(true);
        setContentError('');
        const results = await Promise.all(
          campaigns
            .map((row) => Number(row?.campaignId || 0))
            .filter((id) => Number.isFinite(id) && id > 0)
            .map((campaignId) => fetchContentItems(campaignId))
        );
        setContentRows(results.flat());
      } catch (e) {
        setContentError(e?.message || '콘텐츠 목록을 불러오지 못했습니다.');
        setContentRows([]);
      } finally {
        setContentLoading(false);
      }
    })();
  }, [campaignState?.data?.campaigns, campaignState?.data?.campaign]);

  React.useEffect(() => {
    if (!decisionModalOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setDecisionModalOpen(false);
        setDecisionAction('');
        setDecisionReasonDraft('');
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [decisionModalOpen]);

  React.useEffect(() => {
    if (!decisionModalOpen) return;
    if (String(decisionAction || '').toUpperCase() !== 'APPROVE') return;
    const channels = getTemplateChannels(selected);
    setApproveChannels(channels);
    setApproveDeliverables(() => {
      if (!Array.isArray(channels) || channels.length === 0) return {};
      const out = {};
      for (const ch of channels) {
        const templateCfg = getTemplateConfigForChannel(selected, ch);
        if (!templateCfg) continue;
        out[ch] = {
          type: String(templateCfg?.type || ''),
          requiredCount: String(templateCfg?.requiredCount || '1')
        };
      }
      return out;
    });
  }, [decisionModalOpen, decisionAction, selected]);

  const templateChannels = useMemo(() => {
    return getTemplateChannels(selected);
  }, [selected]);

  const metaInfluencerProfile = useMemo(() => {
    const reqCompanyNo = Number(selected?.requestCompanyNo);
    const direct = getMockMetaInfluencerProfileByCompanyNo(reqCompanyNo);
    if (direct) return direct;
    const fallback =
      Array.isArray(MOCK_META_GRAPH_INFLUENCERS) && MOCK_META_GRAPH_INFLUENCERS.length > 0 ? MOCK_META_GRAPH_INFLUENCERS[0] : null;
    return fallback || null;
  }, [selected?.requestCompanyNo]);

  const [influencerDetailOpen, setInfluencerDetailOpen] = useState(false);

  const influencerDetailGraph = useMemo(() => {
    if (!metaInfluencerProfile?.graph || typeof metaInfluencerProfile.graph !== 'object') return {};
    return metaInfluencerProfile.graph;
  }, [metaInfluencerProfile]);
  const influencerDetailInsights = useMemo(() => {
    if (!metaInfluencerProfile?.insights || typeof metaInfluencerProfile.insights !== 'object') return {};
    return metaInfluencerProfile.insights;
  }, [metaInfluencerProfile]);
  const influencerDetailTags = useMemo(() => {
    if (!metaInfluencerProfile?.tags || typeof metaInfluencerProfile.tags !== 'object') return {};
    return metaInfluencerProfile.tags;
  }, [metaInfluencerProfile]);
  const influencerDetailRecentMedia = useMemo(() => {
    const recent = influencerDetailInsights?.recent_media;
    return Array.isArray(recent) ? recent : [];
  }, [influencerDetailInsights]);
  const influencerDetailTopCountries = useMemo(() => {
    const countries = influencerDetailInsights?.audience?.top_countries;
    return Array.isArray(countries) ? countries : [];
  }, [influencerDetailInsights]);

  const handleCreateTimelineComment = async ({ scope, message, afterCreated }) => {
    if (!selected?.requestId) return;
    const msg = String(message || '').trim();
    if (!msg) return;
    try {
      setTimelineSaving(true);
      await createContractRequestComment({ requestId: selected.requestId, message: msg, scope: String(scope || 'REQUEST') });
      if (typeof afterCreated === 'function') afterCreated();
      await loadTimeline(selected.requestId);
    } catch (e) {
      setTimelineState((prev) => ({ ...prev, error: e?.message || '코멘트 등록에 실패했습니다.' }));
    } finally {
      setTimelineSaving(false);
    }
  };

  const contractTimelineItems = React.useMemo(() => {
    const items = Array.isArray(timelineState.items) ? timelineState.items : [];
    const requestStatusActions = new Set(['REQUESTED', 'REVIEW', 'APPROVE', 'REJECT']);

    const filtered = items.filter((it) => {
      const sc = String(it?.scope || '').trim().toUpperCase();
      const ac = String(it?.action || '').trim().toUpperCase();

      if (!(sc === 'REQUEST' || sc === 'CANCEL' || sc === 'CONDITION' || sc === 'SIGNATURE' || sc === '')) return false;
      if (ac === 'COMMENT') return false;
      if (sc === 'REQUEST' && !requestStatusActions.has(ac)) return false;
      return true;
    });

    const pickedByKey = new Map();
    const order = [];

    const asPositiveId = (v) => {
      return typeof v === 'number' && Number.isFinite(v) && v > 0 ? String(v) : '';
    };

    for (const it of filtered) {
      const sc = String(it?.scope || '').trim().toUpperCase();
      const ac = String(it?.action || '').trim().toUpperCase();
      const actorSide = String(it?.actorSide || '').trim().toUpperCase();
      const createdAt = String(it?.createdAt || '').trim();
      const actorCompanyNo = asPositiveId(it?.actorCompanyNo);
      const actorUserNo = asPositiveId(it?.actorUserNo);
      const contentItemId = asPositiveId(it?.contentItemId);
      const contentRevisionId = asPositiveId(it?.contentRevisionId);

      const eventKey =
        sc === 'REQUEST' && requestStatusActions.has(ac)
          ? [sc, ac].join('|')
          : [sc, ac, actorSide, actorCompanyNo, actorUserNo, createdAt, contentItemId, contentRevisionId].join('|');

      const existing = pickedByKey.get(eventKey);
      if (!existing) {
        const idx = order.length;
        order.push({ key: eventKey, item: it });
        pickedByKey.set(eventKey, { idx, item: it });
        continue;
      }

      const existingMsg = String(existing.item?.message || '').trim();
      const nextMsg = String(it?.message || '').trim();
      const existingCreatedAt = String(existing.item?.createdAt || '').trim();
      const isNextNewer = !!nextMsg && !!existingCreatedAt && !!createdAt && createdAt > existingCreatedAt;
      const existingActor = String(existing.item?.actorSide || '').trim();
      const nextActor = String(it?.actorSide || '').trim();
      const preferNextForActor = !existingActor && !!nextActor;
      if (nextMsg.length > existingMsg.length || isNextNewer || preferNextForActor) {
        order[existing.idx] = { key: eventKey, item: it };
        pickedByKey.set(eventKey, { idx: existing.idx, item: it });
      }
    }

    return order.map((row) => row.item);
  }, [timelineState.items]);

  const getTimelineReactKey = React.useCallback((it, idx) => {
    const commentId = it?.commentId;
    if (typeof commentId === 'number') return `c:${commentId}`;
    const sc = String(it?.scope || '').trim().toUpperCase();
    const ac = String(it?.action || '').trim().toUpperCase();
    const actorSide = String(it?.actorSide || '').trim().toUpperCase();
    const createdAt = String(it?.createdAt || '').trim();
    const asPositiveId = (v) => {
      return typeof v === 'number' && Number.isFinite(v) && v > 0 ? String(v) : '';
    };
    const contentItemId = asPositiveId(it?.contentItemId);
    const contentRevisionId = asPositiveId(it?.contentRevisionId);
    return [sc, ac, actorSide, createdAt, contentItemId, contentRevisionId, String(idx)].join('|');
  }, []);

  const conditionHistoryDiffMap = useMemo(() => {
    const map = {};
    for (const row of Array.isArray(conditionHistoryDiff) ? conditionHistoryDiff : []) {
      const k = String(row?.key || '').trim();
      if (!k) continue;
      map[k] = row;
    }
    return map;
  }, [conditionHistoryDiff]);

  const conditionHistoryOrderedKeys = useMemo(() => {
    return CONDITION_HISTORY_FIELD_DEFS.map((d) => d.key);
  }, [conditionHistorySnapshot, conditionHistoryDiff]);

  const toComparableText = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  };

  const computeConditionDiff = (fromCond, toCond) => {
    const from = fromCond && typeof fromCond === 'object' ? fromCond : {};
    const to = toCond && typeof toCond === 'object' ? toCond : {};
    const keys = new Set([...Object.keys(from), ...Object.keys(to)]);

    const preferredKeys = [
      'sellStartDate',
      'sellEndDate',
      'recruitStartDate',
      'recruitEndDate',
      'deliverableSubmitDeadline',
      'targetCustomer',
      'commissionPolicy',
      'contentCreationSupport',
      'note'
    ];

    const ordered = [...preferredKeys, ...Array.from(keys).filter((k) => !preferredKeys.includes(k))];
    const rows = [];
    for (const k of ordered) {
      if (!keys.has(k)) continue;
      const a = toComparableText(from?.[k]);
      const b = toComparableText(to?.[k]);
      if (a === b) continue;
      rows.push({ key: k, from: a || '-', to: b || '-' });
    }
    return rows;
  };

  const closeConditionHistoryModal = () => {
    setConditionHistoryModalOpen(false);
    setConditionHistoryTitle('');
    setConditionHistoryCreatedAt(null);
    setConditionHistoryActorSide('');
    setConditionHistorySnapshot(null);
    setConditionHistoryDiff([]);
  };

  const openConditionHistoryModal = (it) => {
    const sc = String(it?.scope || '').trim().toUpperCase();
    const ac = String(it?.action || '').trim().toUpperCase();
    if (!(sc === 'CONDITION' && ac === 'PROPOSE')) return;
    const meta = it?.meta && typeof it.meta === 'object' ? it.meta : null;
    const fromCond = meta?.fromCondition && typeof meta.fromCondition === 'object' ? meta.fromCondition : null;
    const toCond = meta?.toCondition && typeof meta.toCondition === 'object' ? meta.toCondition : null;
    if (!toCond) return;

    setConditionHistoryTitle(resolveTimelineLabel(it.scope, it.action));
    setConditionHistoryCreatedAt(it.createdAt || null);
    setConditionHistoryActorSide(String(it.actorSide || ''));
    setConditionHistorySnapshot(toCond);
    setConditionHistoryDiff(computeConditionDiff(fromCond, toCond));
    setConditionHistoryModalOpen(true);
  };

  const closeTimelineCommentModal = () => {
    setTimelineCommentModalOpen(false);
    setTimelineCommentModalTitle('');
    setTimelineCommentModalMessage('');
    setTimelineCommentModalCreatedAt(null);
    setTimelineCommentModalActorSide('');
  };

  const openTimelineCommentModal = (it) => {
    const message = String(it?.message || '').trim();
    if (!message) return;
    setTimelineCommentModalTitle(resolveTimelineLabel(it?.scope, it?.action));
    setTimelineCommentModalMessage(message);
    setTimelineCommentModalCreatedAt(it?.createdAt || null);
    setTimelineCommentModalActorSide(String(it?.actorSide || ''));
    setTimelineCommentModalOpen(true);
  };

  const contentTimelineItems = React.useMemo(() => {
    const items = Array.isArray(timelineState.items) ? timelineState.items : [];
    return items.filter((it) => {
      const sc = String(it?.scope || '').trim().toUpperCase();
      return sc === 'CONTENT' || sc === 'URL';
    });
  }, [timelineState.items]);

  const toggleApproveChannel = (channel) => {
    const next = String(channel || '').trim().toUpperCase();
    if (!next) return;
    setApproveChannels((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.includes(next)) {
        return current.filter((x) => x !== next);
      }
      return [...current, next];
    });

    setApproveDeliverables((prev) => {
      const current = prev && typeof prev === 'object' ? prev : {};
      if (current[next]) return current;
      const options = getDeliverableOptionsByChannel(next);
      const first = options.length > 0 ? options[0].value : '';
      return {
        ...current,
        [next]: {
          type: String(first || ''),
          requiredCount: String(deliverableRequiredCount || '1')
        }
      };
    });
  };

  const setApproveDeliverableField = (channel, field, value) => {
    const key = String(channel || '').trim().toUpperCase();
    if (!key) return;
    setApproveDeliverables((prev) => {
      const current = prev && typeof prev === 'object' ? prev : {};
      const existing = current[key] && typeof current[key] === 'object' ? current[key] : {};
      return {
        ...current,
        [key]: {
          ...existing,
          [field]: value
        }
      };
    });
  };

  const handleCreateCampaign = async () => {
    if (!selected) return;
    if (isCancelApproved) {
      setError('취소된 계약은 캠페인을 생성할 수 없습니다.');
      return;
    }
    try {
      setSavingAction('CREATE_CAMPAIGN');
      setError('');
      const { companyNo } = getUserInfo() || {};
      const trpCompanyNo = Number(companyNo || 0);
      await createCampaignFromContractRequest({ requestId: selected.requestId, trpCompanyNo, channel: 'INSTAGRAM' });
      await loadCampaign(selected.requestId);
      setAlertMessage('캠페인이 생성되었습니다.');
      setAlertModalOpen(true);
    } catch (e) {
      setError(e?.message || '캠페인 생성 중 오류가 발생했습니다.');
    } finally {
      setSavingAction('');
    }
  };

  const handleSaveDeliverable = async () => {
    const campaignId = campaignState?.data?.campaign?.campaignId;
    if (!campaignId) return;
    try {
      setSavingAction('SAVE_DELIVERABLE');
      setError('');
      const requiredCount = Number(String(deliverableRequiredCount || '').trim() || 0);
      if (!Number.isFinite(requiredCount) || requiredCount <= 0) {
        setError('필수 개수는 1 이상이어야 합니다.');
        return;
      }
      await upsertDeliverable({ campaignId, type: deliverableType, requiredCount });
      await loadCampaign(selected?.requestId);
      setAlertMessage('작업물이 저장되었습니다.');
      setAlertModalOpen(true);
    } catch (e) {
      setError(e?.message || '작업물 저장 중 오류가 발생했습니다.');
    } finally {
      setSavingAction('');
    }
  };

  const openContentDetail = async (contentItemId) => {
    if (!contentItemId) return;
    if (isCancelApproved) {
      setContentError('취소된 계약은 콘텐츠를 등록/조회할 수 없습니다.');
      return;
    }
    try {
      setContentDetailOpen(true);
      setContentDetailLoading(true);
      setContentDetail(null);
      setContentReviewNote('');
      setUrlReviewNote('');
      setUrlRejectAlsoRequestCopyRevision(false);
      setContentShowPlan(true);
      const data = await fetchContentItemDetail(contentItemId);
      setContentDetail(data);
      const latest = Array.isArray(data?.revisions) && data.revisions.length > 0 ? data.revisions[0] : null;
      setContentReviewNote(String(latest?.reviewNote || ''));
      const url = String(data?.item?.publishedUrl || '').trim();
      if (url) setContentShowPlan(false);
    } catch (e) {
      setContentDetail({ error: e?.message || '콘텐츠 상세를 불러오지 못했습니다.' });
    } finally {
      setContentDetailLoading(false);
    }
  };

  const handleContentReview = async (action, reviewNoteOverride) => {
    const contentItemId = contentDetail?.item?.contentItemId;
    if (!contentItemId) return;
    try {
      setSavingAction(action);
      setError('');
      const reviewNote = reviewNoteOverride === undefined ? contentReviewNote : reviewNoteOverride;
      await reviewContentItem({ contentItemId, action, reviewNote });
      const campaigns = Array.isArray(campaignState?.data?.campaigns)
        ? campaignState.data.campaigns
        : campaignState?.data?.campaign
          ? [campaignState.data.campaign]
          : [];
      const firstCampaignId = Number(campaigns[0]?.campaignId || 0);
      if (Number.isFinite(firstCampaignId) && firstCampaignId > 0) await loadCampaign(selected?.requestId);
      setContentDetailOpen(false);
      setContentDetail(null);
      setContentReviewNote('');
      setUrlReviewNote('');
      setUrlRejectAlsoRequestCopyRevision(false);
      if (selected?.requestId) await loadTimeline(selected.requestId);

      if (String(action || '').toUpperCase() === 'REQUEST_REVISION') {
        setAlertMessage('수정요청이 완료되었습니다.');
        setAlertModalOpen(true);
      }
    } catch (e) {
      setError(e?.message || '콘텐츠 검토 중 오류가 발생했습니다.');
    } finally {
      setSavingAction('');
    }
  };

  const openContentRevisionRequestModal = () => {
    setContentRevisionRequestNoteDraft('');
    setContentRevisionRequestModalOpen(true);
  };

  const closeContentRevisionRequestModal = () => {
    if (!!savingAction) return;
    setContentRevisionRequestModalOpen(false);
    setContentRevisionRequestNoteDraft('');
  };

  const submitContentRevisionRequest = async () => {
    const note = String(contentRevisionRequestNoteDraft || '').trim();
    if (!note) {
      setError('수정요청 사유를 입력하세요.');
      return;
    }
    setContentRevisionRequestModalOpen(false);
    setContentRevisionRequestNoteDraft('');
    await handleContentReview('REQUEST_REVISION', note);
  };

  const handleUrlReview = async (action) => {
    const contentItemId = contentDetail?.item?.contentItemId;
    if (!contentItemId) return;
    try {
      const normalized = String(action || '').toUpperCase();
      setSavingAction(normalized === 'APPROVE' ? 'URL_APPROVE' : 'URL_REQUEST_REVISION');
      setError('');
      await reviewContentItemUrl({ contentItemId, action, reviewNote: urlReviewNote });

      if (normalized === 'REQUEST_REVISION' && urlRejectAlsoRequestCopyRevision) {
        await reviewContentItem({ contentItemId, action: 'REQUEST_REVISION', reviewNote: contentReviewNote });
      }

      const campaigns = Array.isArray(campaignState?.data?.campaigns)
        ? campaignState.data.campaigns
        : campaignState?.data?.campaign
          ? [campaignState.data.campaign]
          : [];
      const firstCampaignId = Number(campaigns[0]?.campaignId || 0);
      if (Number.isFinite(firstCampaignId) && firstCampaignId > 0) await loadCampaign(selected?.requestId);
      setContentDetailOpen(false);
      setContentDetail(null);
      setContentReviewNote('');
      setUrlReviewNote('');
      setUrlRejectAlsoRequestCopyRevision(false);
      if (selected?.requestId) await loadTimeline(selected.requestId);
    } catch (e) {
      setError(e?.message || 'URL 검토 중 오류가 발생했습니다.');
    } finally {
      setSavingAction('');
    }
  };

  const handleCancelRequest = async () => {
    if (!selected) return;
    try {
      setCancelSaving(true);
      setError('');
      const reason = String(cancelReason || '').trim();
      if (!reason) {
        setCancelError('취소 사유를 입력하세요.');
        return;
      }
      setCancelError('');
      const updated = await requestContractCancel({ requestId: selected.requestId, reason });
      setSelected(updated);
      setAlertMessage('취소요청이 완료되었습니다.');
      setAlertModalOpen(true);
      await loadSelected();
      await loadTimeline(selected.requestId);
      setCancelModalOpen(false);
      setCancelReason('');
    } catch (e) {
      setCancelError(e?.message || '취소 요청 중 오류가 발생했습니다.');
    } finally {
      setCancelSaving(false);
    }
  };

  const handleCancelAction = async (action) => {
    if (!selected) return;
    try {
      setSavingAction(action);
      setError('');
      const updated = await updateContractCancelStatus({
        requestId: selected.requestId,
        action,
        reviewNote: cancelReviewNote
      });
      setSelected(updated);
      setCancelReviewNote('');
      await loadSelected();
      await loadTimeline(selected.requestId);
    } catch (e) {
      setError(e?.message || '취소 처리 중 오류가 발생했습니다.');
    } finally {
      setSavingAction('');
    }
  };

  const handleDecisionSubmit = async () => {
    if (!selected) return;
    const action = String(decisionAction || '').toUpperCase();
    if (!action) return;

    try {
      setSavingAction(action);
      setError('');
      const reason = String(decisionReasonDraft || '').trim();
      if (reason.length === 0) {
        setError(action === 'APPROVE' ? '승인 사유를 입력하세요.' : '반려 사유를 입력하세요.');
        return;
      }

      if (action === 'APPROVE') {
        const { companyNo } = getUserInfo() || {};
        const trpCompanyNo = Number(companyNo || 0);
        const productNo = Number(selected?.productNo || 0);

        if (!Number.isFinite(trpCompanyNo) || trpCompanyNo <= 0) {
          setError('로그인 회사 정보를 확인할 수 없습니다.');
          return;
        }
        if (!Number.isFinite(productNo) || productNo <= 0) {
          setError('상품번호를 확인할 수 없습니다.');
          return;
        }

        let deliverableTemplate = extractDeliverableTemplate(selected);
        if (!deliverableTemplate || typeof deliverableTemplate !== 'object' || Object.keys(deliverableTemplate).length === 0) {
          const sc = await getSalesCondition({ companyNo: trpCompanyNo, productNo });
          deliverableTemplate =
            sc?.필수작업물템플릿 && typeof sc.필수작업물템플릿 === 'object' ? sc.필수작업물템플릿 : {};
        }

        const channels = getTemplateChannels({
          ...selected,
          requestedSalesCondition: {
            ...(selected.requestedSalesCondition && typeof selected.requestedSalesCondition === 'object'
              ? selected.requestedSalesCondition
              : {}),
            deliverableTemplate
          }
        });

        if (!Array.isArray(channels) || channels.length === 0) {
          setError('판매조건에 등록된 필수 작업물 템플릿이 없습니다. 판매조건 설정에서 템플릿을 먼저 등록하세요.');
          return;
        }

        for (const channel of channels) {
          await createCampaignFromContractRequest({ requestId: selected.requestId, trpCompanyNo, channel });
        }
      }

      const updated = await updateContractRequestStatus({
        requestId: selected.requestId,
        action,
        reviewNote: reason
      });
      setSelected(updated);
      setDecisionModalOpen(false);
      setDecisionAction('');
      setDecisionReasonDraft('');
      await loadSelected();
      if (action === 'APPROVE') {
        await loadCampaign(selected.requestId);
      }
      await loadTimeline(selected.requestId);
    } catch (e) {
      setError(e?.message || '상태 변경 중 오류가 발생했습니다.');
    } finally {
      setSavingAction('');
    }
  };

  const cancelStatus = String(selected?.cancelStatus || 'NONE').toUpperCase();
  const isCancelRequested = selected && cancelStatus === 'REQUESTED';
  const isCancelApproved = selected && cancelStatus === 'APPROVED';

  useEffect(() => {
    if (!isCancelApproved) return;
    setContentDetailOpen(false);
    setContentDetail(null);
  }, [isCancelApproved]);

  const { companyNo: myCompanyNo } = getUserInfo() || {};
  const requesterCompanyNo = Number(selected?.cancelRequestedByCompanyNo || 0);
  const isCancelRequester =
    isCancelRequested && Number(myCompanyNo || 0) > 0 && Number(myCompanyNo || 0) === requesterCompanyNo;

  React.useEffect(() => {
    const productNo = Number(selected?.productNo);
    const companyNo = Number(myCompanyNo || 0);
    if (!Number.isFinite(productNo) || productNo <= 0) {
      setIsNegotiable(false);
      return;
    }
    if (!Number.isFinite(companyNo) || companyNo <= 0) {
      setIsNegotiable(false);
      return;
    }

    let mounted = true;
    const run = async () => {
      try {
        const condition = await getSalesCondition({ companyNo, productNo });
        if (!mounted) return;
        setIsNegotiable(Boolean(condition?.협의가능));
      } catch {
        if (!mounted) return;
        setIsNegotiable(false);
      }
    };

    void run();
    return () => {
      mounted = false;
    };
  }, [selected?.productNo, myCompanyNo]);

  const contractStatus = String(selected?.status || '').toUpperCase();
  const conditionPendingSide = String(selected?.conditionPendingSide || 'TRP').trim().toUpperCase();

  const hasIncomingConditionProposal = useMemo(() => {
    const proposed = selected?.proposedSalesCondition && typeof selected.proposedSalesCondition === 'object'
      ? selected.proposedSalesCondition
      : null;
    if (!proposed) return false;
    if (!selected?.requestedSalesCondition) return false;
    if (conditionPendingSide !== 'TRP') return false;
    const keys = ['sellStartDate', 'sellEndDate', 'deliverableSubmitDeadline', 'commissionPolicy', 'contentCreationSupport'];
    return keys.some((k) => {
      const v = proposed?.[k];
      return String(v ?? '').trim().length > 0;
    });
  }, [selected, conditionPendingSide]);

  const decisionStageVisible =
    selected && !isCancelApproved && (contractStatus === 'REQUESTED' || contractStatus === 'REVIEWED');

  const decisionBlockReason = React.useMemo(() => {
    if (!decisionStageVisible) return '';
    if (isCancelRequested) return '취소 요청 처리 후 계약 진행을 결정할 수 있습니다.';
    if (conditionPendingSide !== 'TRP') {
      const pendingLabel = resolveActorLabel(conditionPendingSide);
      const safeLabel = pendingLabel && pendingLabel !== '-' ? pendingLabel : '상대방';
      return `${safeLabel} 측 응답 대기 상태입니다.`;
    }
    return '';
  }, [decisionStageVisible, isCancelRequested, conditionPendingSide]);

  const negotiableForProposal =
    !!(selected?.requestedSalesCondition && typeof selected.requestedSalesCondition === 'object'
      ? selected.requestedSalesCondition.negotiable
      : null) || isNegotiable;

  const proposeBlockReason = React.useMemo(() => {
    if (!decisionStageVisible) return '';
    if (!negotiableForProposal) return '협의 불가 판매조건입니다.';
    return decisionBlockReason;
  }, [decisionStageVisible, negotiableForProposal, decisionBlockReason]);

  const canDecide =
    selected &&
    !isCancelRequested &&
    !isCancelApproved &&
    (contractStatus === 'REQUESTED' || contractStatus === 'REVIEWED') &&
    conditionPendingSide === 'TRP';

  const canProposeContractCondition =
    selected &&
    negotiableForProposal &&
    !isCancelRequested &&
    !isCancelApproved &&
    (contractStatus === 'REQUESTED' || contractStatus === 'REVIEWED') &&
    conditionPendingSide === 'TRP';

  const openConditionModal = () => {
    const proposed =
      selected?.proposedSalesCondition && typeof selected.proposedSalesCondition === 'object' ? selected.proposedSalesCondition : {};
    const requested =
      selected?.requestedSalesCondition && typeof selected.requestedSalesCondition === 'object' ? selected.requestedSalesCondition : {};

    setConditionError('');
    setConditionMessage('');
    const sellStartDateDraft = typeof proposed?.sellStartDate === 'string' ? String(proposed.sellStartDate) : '';
    const sellEndDateDraft = typeof proposed?.sellEndDate === 'string' ? String(proposed.sellEndDate) : '';
    const deliverableSubmitDeadlineDraft =
      typeof proposed?.deliverableSubmitDeadline === 'string' ? String(proposed.deliverableSubmitDeadline) : '';
    const commissionPolicyDraft = typeof proposed?.commissionPolicy === 'string' ? String(proposed.commissionPolicy) : '';
    const contentCreationSupportDraft =
      typeof proposed?.contentCreationSupport === 'string' ? String(proposed.contentCreationSupport) : '';

    const sellStartDateBase = typeof requested?.sellStartDate === 'string' ? String(requested.sellStartDate) : '';
    const sellEndDateBase = typeof requested?.sellEndDate === 'string' ? String(requested.sellEndDate) : '';
    const deliverableSubmitDeadlineBase =
      typeof requested?.deliverableSubmitDeadline === 'string' ? String(requested.deliverableSubmitDeadline) : '';
    const commissionPolicyBase = typeof requested?.commissionPolicy === 'string' ? String(requested.commissionPolicy) : '';
    const contentCreationSupportBase =
      typeof requested?.contentCreationSupport === 'string' ? String(requested.contentCreationSupport) : '';

    setConditionSellStartDate(sellStartDateDraft || sellStartDateBase);
    setConditionSellEndDate(sellEndDateDraft || sellEndDateBase);
    setConditionDeliverableSubmitDeadline(deliverableSubmitDeadlineDraft || deliverableSubmitDeadlineBase);
    setConditionCommissionPolicy(commissionPolicyDraft || commissionPolicyBase);
    setConditionContentCreationSupport(
      contentCreationSupportDraft || contentCreationSupportBase
    );
    setConditionModalOpen(true);
  };

  const handleSubmitConditionProposal = async () => {
    if (!selected?.requestId) return;
    if (!canProposeContractCondition) {
      setConditionError('계약조건 제안이 불가합니다.');
      return;
    }

    const requested =
      selected?.requestedSalesCondition && typeof selected.requestedSalesCondition === 'object' ? selected.requestedSalesCondition : {};

    const payload = {};
    const sellStartDate = String(conditionSellStartDate || '').trim();
    const sellEndDate = String(conditionSellEndDate || '').trim();
    const deliverableSubmitDeadline = String(conditionDeliverableSubmitDeadline || '').trim();
    const commissionPolicy = String(conditionCommissionPolicy || '').trim();
    const contentCreationSupport = String(conditionContentCreationSupport || '').trim();

    const baseSellStartDate = typeof requested?.sellStartDate === 'string' ? String(requested.sellStartDate).trim() : '';
    const baseSellEndDate = typeof requested?.sellEndDate === 'string' ? String(requested.sellEndDate).trim() : '';
    const baseDeliverableSubmitDeadline =
      typeof requested?.deliverableSubmitDeadline === 'string' ? String(requested.deliverableSubmitDeadline).trim() : '';
    const baseCommissionPolicy = typeof requested?.commissionPolicy === 'string' ? String(requested.commissionPolicy).trim() : '';
    const baseContentCreationSupport =
      typeof requested?.contentCreationSupport === 'string' ? String(requested.contentCreationSupport).trim() : '';

    if (sellStartDate && sellStartDate !== baseSellStartDate) payload.sellStartDate = sellStartDate;
    if (sellEndDate && sellEndDate !== baseSellEndDate) payload.sellEndDate = sellEndDate;
    if (deliverableSubmitDeadline && deliverableSubmitDeadline !== baseDeliverableSubmitDeadline)
      payload.deliverableSubmitDeadline = deliverableSubmitDeadline;
    if (commissionPolicy && commissionPolicy !== baseCommissionPolicy) payload.commissionPolicy = commissionPolicy;
    if (contentCreationSupport && contentCreationSupport !== baseContentCreationSupport)
      payload.contentCreationSupport = contentCreationSupport;

    if (Object.keys(payload).length === 0) {
      setConditionError('제안할 계약조건을 최소 1개 이상 입력하세요.');
      return;
    }

    try {
      setConditionSaving(true);
      setConditionError('');
      const updated = await proposeContractRequestCondition({
        requestId: selected.requestId,
        proposedSalesCondition: payload,
        message: String(conditionMessage || '')
      });
      setSelected(updated);
      setConditionModalOpen(false);
      setConditionMessage('');
      setConditionSellStartDate('');
      setConditionSellEndDate('');
      setConditionDeliverableSubmitDeadline('');
      setConditionCommissionPolicy('');
      setConditionContentCreationSupport('');
      await loadSelected();
      await loadTimeline(selected.requestId);
      setAlertMessage('제안이 완료되었습니다.');
      setAlertModalOpen(true);
    } catch (e) {
      setConditionError(e?.message || '계약조건 제안에 실패했습니다.');
    } finally {
      setConditionSaving(false);
    }
  };

  const isContractApproved = selected && String(selected.status || '').toUpperCase() === 'APPROVED';
  const isAllSigned = Boolean(String(selected?.trpSignedAt || '').trim()) && Boolean(String(selected?.crsSignedAt || '').trim());
  const canCreateCampaign = Boolean(isContractApproved && isAllSigned);

  const campaign = campaignState?.data?.campaign || null;
  const deliverables = Array.isArray(campaignState?.data?.deliverables) ? campaignState.data.deliverables : [];

  const contractDeliverableSummary = useMemo(() => {
    if (!selected) return '';
    const templateChannels = getApprovalChannels(selected);
    const templateLines = (Array.isArray(templateChannels) ? templateChannels : [])
      .map((ch) => {
        const cfg = getTemplateConfigForChannel(selected, ch);
        const type = String(cfg?.type || '').trim();
        const requiredCount = Number(String((cfg?.requiredCount ?? cfg?.required_count) ?? '').trim() || 0);
        if (!type || !Number.isFinite(requiredCount) || requiredCount <= 0) return '';
        return `${String(ch)}: ${resolveDeliverableLabel(type)} ${requiredCount}개`;
      })
      .filter(Boolean);

    const campaignLines = (Array.isArray(deliverables) ? deliverables : [])
      .map((d) => {
        const type = String(d?.type || '').trim();
        const requiredCount = Number((d?.requiredCount ?? d?.required_count) || 0);
        if (!type || !Number.isFinite(requiredCount) || requiredCount <= 0) return '';
        return `${resolveDeliverableLabel(type)} ${requiredCount}개`;
      })
      .filter(Boolean);

    return templateLines.length > 0 ? templateLines.join('\n') : campaignLines.length > 0 ? campaignLines.join('\n') : '';
  }, [selected, deliverables]);

  const handlePrintContract = () => {
    const node = contractDocRef.current;
    if (!node) return;
    const html = node.outerHTML;
    const w = window.open('', '_blank');
    if (!w) return;

    const headNodes = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'));
    const headHtml = headNodes
      .map((el) => {
        const tag = String(el.tagName || '').toLowerCase();
        if (tag === 'link') {
          const href = el.getAttribute('href');
          if (!href) return '';
          return `<link rel="stylesheet" href="${href}" />`;
        }
        return `<style>${el.innerHTML || ''}</style>`;
      })
      .filter(Boolean)
      .join('\n');

    w.document.open();
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width, initial-scale=1" /><title>계약서</title>
${headHtml}
<style>
  html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; padding: 16px; background: #fff; }
</style>
</head><body>${html}</body></html>`);
    w.document.close();

    void (async () => {
      const links = Array.from(w.document.querySelectorAll('link[rel="stylesheet"]'));
      await Promise.all(
        links.map(
          (l) =>
            new Promise((resolve) => {
              l.addEventListener('load', resolve, { once: true });
              l.addEventListener('error', resolve, { once: true });
            })
        )
      );
      try {
        if (w.document.fonts && w.document.fonts.ready) {
          await w.document.fonts.ready;
        }
      } catch {}
      w.focus();
      w.print();
    })();
  };

  useEffect(() => {
    if (!contractDocOpen) return;
    if (!selected) return;
    let mounted = true;

    (async () => {
      try {
        const supplier = await getCompanyBasicInfo({ companyNo: getUserInfo()?.companyNo });
        if (mounted) setContractSupplierInfo(supplier || null);
      } catch {
        if (mounted) setContractSupplierInfo(null);
      }

      try {
        const reqCompanyNo = Number(selected?.requestCompanyNo || 0);
        const reqUserNo = Number(selected?.requestUserNo || 0);
        if (reqCompanyNo > 0 && reqUserNo > 0) {
          const influencer = await getCompanyUserInfo({ companyNo: reqCompanyNo, userNo: reqUserNo });
          if (mounted) setContractInfluencerInfo(influencer || null);
        } else if (mounted) {
          setContractInfluencerInfo(null);
        }
      } catch {
        if (mounted) setContractInfluencerInfo(null);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [contractDocOpen, selected]);

  const pickFirstNonEmpty = (row, keys) => {
    const obj = row && typeof row === 'object' ? row : {};
    for (const k of keys) {
      const v = String(obj?.[k] ?? '').trim();
      if (v) return v;
    }
    return '';
  };

  const handleSignContract = async () => {
    if (!selected?.requestId) return;
    try {
      setSavingAction('SIGN');
      setError('');
      const updated = await signContractRequest({ requestId: selected.requestId });
      setSelected(updated);
      setContractAgreeChecked(false);
      await loadSelected();
      await loadTimeline(selected.requestId);
      setAlertMessage('서명이 완료되었습니다.');
      setAlertModalOpen(true);
    } catch (e) {
      setError(e?.message || '서명 처리 중 오류가 발생했습니다.');
    } finally {
      setSavingAction('');
    }
  };
  const unmetDeliverables = Array.isArray(campaignState?.data?.unmetDeliverables) ? campaignState.data.unmetDeliverables : [];
  const isAllDeliverablesPublished = !!campaignState?.data?.isAllDeliverablesPublished;

  const deliverableSlots = useMemo(() => {
    const ds = Array.isArray(deliverables) ? deliverables : [];
    const items = Array.isArray(contentRows) ? contentRows : [];
    const byDeliverable = new Map();
    for (const it of items) {
      const deliverableId = Number(it?.deliverableId);
      if (!Number.isFinite(deliverableId) || deliverableId <= 0) continue;
      const list = byDeliverable.get(deliverableId) || [];
      list.push(it);
      byDeliverable.set(deliverableId, list);
    }
    for (const [k, list] of byDeliverable.entries()) {
      list.sort((a, b) => {
        const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        if (aTime !== bTime) return aTime - bTime;
        return Number(a?.contentItemId) - Number(b?.contentItemId);
      });
      byDeliverable.set(k, list);
    }

    return ds.flatMap((d) => {
      const deliverableId = Number(d?.deliverableId);
      const required = Math.max(0, Number((d?.requiredCount ?? d?.required_count) || 0));
      const list = byDeliverable.get(deliverableId) || [];
      const slotCount = Math.max(required, list.length);
      return Array.from({ length: slotCount }, (_, i) => {
        return {
          deliverableId,
          type: d?.type,
          requiredCount: required,
          slotIndex: i,
          item: list[i] || null
        };
      });
    });
  }, [deliverables, contentRows]);

  const nextActionGuide = useMemo(() => {
    if (!selected) return { key: 'NONE' };
    if (isCancelApproved) return { key: 'CANCELLED' };
    if (isCancelRequested) return { key: 'CANCEL_REQUESTED' };

    if (decisionStageVisible) {
      if (conditionPendingSide === 'TRP') {
        return { key: 'DECIDE_CONTRACT' };
      }
      return { key: 'WAIT_CONTRACT_DECISION', pendingSide: conditionPendingSide || 'CRS' };
    }

    if (String(contractStatus || '').toUpperCase() === 'REJECTED') {
      return { key: 'CONTRACT_REJECTED', note: String(selected?.reviewNote || '').trim() };
    }

    if (String(contractStatus || '').toUpperCase() !== 'APPROVED') {
      return { key: 'WAIT_CONTRACT_PROGRESS' };
    }

    const trpSignedAt = selected?.trpSignedAt;
    const crsSignedAt = selected?.crsSignedAt;
    const isTrpSigned = Boolean(String(trpSignedAt || '').trim());
    const isCrsSigned = Boolean(String(crsSignedAt || '').trim());
    const isAllSigned = isTrpSigned && isCrsSigned;

    if (!isAllSigned) {
      if (!isTrpSigned) {
        return { key: 'SIGN_CONTRACT' };
      }
      return { key: 'WAIT_CRS_SIGNATURE' };
    }

    if (!campaign) {
      return { key: 'CREATE_CAMPAIGN' };
    }

    const items = Array.isArray(contentRows) ? contentRows : [];
    const isDrafting =
      items.length === 0 ||
      items.some((it) => {
        const s = normalizeContentStatus(it?.status);
        return s === 'CREATED' || s === 'DRAFT';
      });

    const firstContentSubmitted = items.find((it) => normalizeContentStatus(it?.status) === 'SUBMITTED');
    if (firstContentSubmitted) {
      const cid = Number(firstContentSubmitted?.contentItemId);
      if (Number.isFinite(cid) && cid > 0) {
        return { key: 'REVIEW_CONTENT_PLAN', contentItemId: cid };
      }
    }

    const firstUrlSubmitted = items.find((it) => String(it?.urlStatus || '').trim().toUpperCase() === 'SUBMITTED');
    if (firstUrlSubmitted) {
      const cid = Number(firstUrlSubmitted?.contentItemId);
      if (Number.isFinite(cid) && cid > 0) {
        return { key: 'REVIEW_URL', contentItemId: cid };
      }
    }

    const revisionRequested = items.find((it) => normalizeContentStatus(it?.status) === 'REVISION_REQUESTED');
    if (revisionRequested) {
      return { key: 'WAIT_CONTENT_REVISION' };
    }

    const urlRejected = items.find((it) => String(it?.urlStatus || '').trim().toUpperCase() === 'REJECTED');
    if (urlRejected) {
      return { key: 'WAIT_URL_RESUBMIT' };
    }

    const waitingUrlSubmit = items.find((it) => {
      const s = normalizeContentStatus(it?.status);
      const u = normalizeUrlStatus(it?.urlStatus);
      return s === 'APPROVED' && (u === 'NONE' || u === 'UNKNOWN');
    });
    if (waitingUrlSubmit) {
      return { key: 'WAIT_URL_SUBMIT' };
    }

    if (isDrafting) {
      return { key: 'WAIT_CRS_DRAFTING' };
    }

    return { key: 'ALL_CLEAR' };
  }, [selected, isCancelApproved, isCancelRequested, decisionStageVisible, conditionPendingSide, contractStatus, campaign, contentRows]);

  const actionGuideButtonLabel = useMemo(() => {
    if (nextActionGuide.key === 'DECIDE_CONTRACT') {
      return `계약조건 검토(${resolveActorLabel('TRP')})`;
    }
    if (nextActionGuide.key === 'WAIT_CONTRACT_DECISION') {
      const pendingLabel = resolveActorLabel(nextActionGuide.pendingSide);
      const safeLabel = pendingLabel && pendingLabel !== '-' ? pendingLabel : '상대방';
      return `계약조건 검토(${safeLabel})`;
    }
    if (nextActionGuide.key === 'WAIT_CONTENT_REVISION') {
      return '해야할 일';
    }
    return '해야할 일';
  }, [nextActionGuide]);

  const parseYmd = (ymd) => {
    const raw = typeof ymd === 'string' ? ymd.trim() : '';
    if (!raw) return null;
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const y = Number(m[1]);
    const mm = Number(m[2]);
    const d = Number(m[3]);
    const dt = new Date(y, mm - 1, d);
    return Number.isFinite(dt.getTime()) ? dt : null;
  };

  const startOfToday = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  };

  const resolveSalePhase = (sellStartDate, sellEndDate) => {
    const start = parseYmd(sellStartDate);
    const end = parseYmd(sellEndDate);
    if (!start || !end) return 'UNKNOWN';
    const today = startOfToday();
    const endInclusive = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);
    if (today < start) return 'BEFORE';
    if (today > endInclusive) return 'ENDED';
    return 'ONGOING';
  };

  const salePhase = useMemo(() => {
    const requested = selected?.requestedSalesCondition && typeof selected.requestedSalesCondition === 'object'
      ? selected.requestedSalesCondition
      : {};
    return resolveSalePhase(requested?.sellStartDate, requested?.sellEndDate);
  }, [selected]);

  const contractProgress = useMemo(() => {
    const build = (currentStepKey, helperText, nextStepKey = null, options = {}) => ({ currentStepKey, helperText, nextStepKey, ...options });

    if (!selected) return build('REQUEST', '계약 정보를 확인 중입니다.');
    if (isCancelApproved) return build('APPROVAL', '계약 취소가 승인되어 진행이 종료되었습니다.');

    if (decisionStageVisible) {
      if (conditionPendingSide === 'TRP') {
        return build('REQUEST', '계약 신청이 접수되었습니다. 승인, 반려 또는 계약조건 재제안을 진행할 수 있습니다.', 'APPROVAL');
      }
      return build('REQUEST', '계약조건 수정 제안을 전달했고 상대방의 응답을 기다리는 단계입니다.', 'APPROVAL', {
        currentStepTitle: '계약조건 제안 대기'
      });
    }

    if (String(contractStatus || '').toUpperCase() === 'REJECTED') {
      return build('APPROVAL', '계약이 반려된 상태입니다. 반려 사유와 이전 진행 이력을 확인하세요.', null, {
        currentStepTitle: '계약 반려',
        visibleStepKeys: ['REQUEST', 'APPROVAL'],
        stepLabelOverrides: {
          APPROVAL: '계약 반려'
        },
        tone: 'danger'
      });
    }

    if (String(contractStatus || '').toUpperCase() !== 'APPROVED') {
      return build('REQUEST', '계약 진행 상태를 확인 중입니다.');
    }

    const isTrpSigned = Boolean(String(selected?.trpSignedAt || '').trim());
    const isCrsSigned = Boolean(String(selected?.crsSignedAt || '').trim());
    const isFullySigned = isTrpSigned && isCrsSigned;

    if (!isFullySigned) {
      return build(
        'SIGNATURE',
        isTrpSigned
          ? '공급사 서명이 완료되었습니다. 인플루언서 서명을 기다리는 단계입니다.'
          : '계약 승인이 완료되었습니다. 계약서를 검토하고 서명하세요.',
        'PLAN_WRITE'
      );
    }

    if (!campaign) {
      return build('SIGNATURE', '계약서 서명이 완료되었습니다. 이제 캠페인을 생성할 차례입니다.', 'PLAN_WRITE');
    }

    const items = Array.isArray(contentRows) ? contentRows : [];
    const hasAnyContentItem = items.length > 0;
    const hasDraftInProgress = items.some((it) => {
      const s = normalizeContentStatus(it?.status);
      return s === 'CREATED' || s === 'DRAFT' || s === 'REJECTED' || s === 'REVISION_REQUESTED';
    });
    const hasSubmittedPlan = items.some((it) => normalizeContentStatus(it?.status) === 'SUBMITTED');
    const allPlansApproved = hasAnyContentItem && items.every((it) => {
      const s = normalizeContentStatus(it?.status);
      return s === 'APPROVED' || s === 'PUBLISHED';
    });
    const hasApprovedPlanWaitingUrl = items.some((it) => {
      const s = normalizeContentStatus(it?.status);
      const u = normalizeUrlStatus(it?.urlStatus);
      return s === 'APPROVED' && (u === 'NONE' || u === 'REJECTED' || u === 'UNKNOWN');
    });
    const hasApprovedPlan = items.some((it) => normalizeContentStatus(it?.status) === 'APPROVED' || normalizeContentStatus(it?.status) === 'PUBLISHED');
    const hasSubmittedUrl = items.some((it) => normalizeUrlStatus(it?.urlStatus) === 'SUBMITTED');
    const hasApprovedUrl = items.some((it) => normalizeUrlStatus(it?.urlStatus) === 'APPROVED');
    const allUrlsSubmitted = hasAnyContentItem && items.every((it) => {
      const u = normalizeUrlStatus(it?.urlStatus);
      return u === 'SUBMITTED' || u === 'APPROVED';
    });
    const allUrlsApproved = hasAnyContentItem && items.every((it) => normalizeUrlStatus(it?.urlStatus) === 'APPROVED');
    const hasDeliverableRequirement = deliverables.length > 0;
    const allRequiredPublished = hasDeliverableRequirement && unmetDeliverables.length === 0 && hasApprovedUrl;

    if (!hasAnyContentItem) {
      return build('PLAN_WRITE', '콘텐츠 기획안 작성 전 단계입니다. 인플루언서가 기획안 작성을 시작하면 이력이 표시됩니다.', 'PLAN_APPROVAL');
    }

    if (!allPlansApproved) {
      if (hasDraftInProgress) {
        const hasRevisionRequestedPlan = items.some((it) => normalizeContentStatus(it?.status) === 'REVISION_REQUESTED');
        return build('PLAN_WRITE', '인플루언서가 콘텐츠 기획안을 작성하거나 수정 중입니다. 제출 후 검토를 진행할 수 있습니다.', 'PLAN_APPROVAL', hasRevisionRequestedPlan ? {
          currentStepTitle: '콘텐츠 기획안 작성 대기',
          stepLabelOverrides: {
            PLAN_WRITE: '콘텐츠 기획안 작성 대기'
          }
        } : {});
      }

      if (hasSubmittedPlan) {
        return build('PLAN_APPROVAL', '콘텐츠 기획안이 제출되었습니다. 모든 콘텐츠 기획안이 승인되기 전까지는 승인 단계로 표시됩니다.', 'CONTENT_REGISTER', {
          currentStepTitle: '콘텐츠 기획안 승인 대기'
        });
      }

      return build('PLAN_WRITE', '콘텐츠 기획안 진행 상태를 확인 중입니다.', 'PLAN_APPROVAL');
    }

    if (!allUrlsSubmitted || hasApprovedPlanWaitingUrl) {
      return build('CONTENT_REGISTER', '기획안 승인이 완료되었습니다. 모든 콘텐츠의 게시 URL을 등록해야 다음 단계로 넘어갑니다.', 'CONTENT_APPROVAL');
    }

    if (!allUrlsApproved || hasSubmittedUrl) {
      return build('CONTENT_APPROVAL', '등록된 콘텐츠 URL을 검토 중입니다. 모든 콘텐츠가 승인되면 캠페인이 진행 단계로 넘어갑니다.', 'CAMPAIGN_START');
    }

    if (salePhase === 'ENDED') {
      return build('CAMPAIGN_END', '캠페인 기간이 종료되었습니다. 최종 상태와 이력을 확인하세요.');
    }

    if (salePhase === 'ONGOING' || allRequiredPublished) {
      return build('CAMPAIGN_START', '필수 콘텐츠 승인이 완료되어 캠페인이 진행 중입니다. 종료일까지 운영 상태를 확인하세요.', 'CAMPAIGN_END');
    }

    return build('SIGNATURE', '계약 이후 캠페인 진행 상태를 확인 중입니다.');
  }, [selected, isCancelApproved, decisionStageVisible, conditionPendingSide, contractStatus, campaign, contentRows, deliverables, unmetDeliverables, salePhase]);

  useEffect(() => {
    const contentFirstStepKeys = ['PLAN_WRITE', 'PLAN_APPROVAL', 'CONTENT_REGISTER', 'CONTENT_APPROVAL', 'CAMPAIGN_START', 'CAMPAIGN_END'];
    const defaultTab = contentFirstStepKeys.includes(contractProgress.currentStepKey) ? 'CONTENT' : 'CONDITION';
    setActiveTab((prev) => (prev === defaultTab ? prev : defaultTab));
  }, [contractProgress.currentStepKey]);

  const effectiveVisibleSteps = useMemo(() => {
    return Array.isArray(contractProgress.visibleStepKeys) && contractProgress.visibleStepKeys.length > 0
      ? contractProgress.visibleStepKeys
      : ContractProgressSteps.map((step) => step.key);
  }, [contractProgress.visibleStepKeys]);

  const visibleGroupKeys = useMemo(() => {
    const groups = ContractProgressGroups.filter((g) => effectiveVisibleSteps.some((k) => g.stepKeys.includes(k)));
    return groups.map((g) => g.key);
  }, [effectiveVisibleSteps]);

  const currentProgressGroupKey = useMemo(() => {
    const candidate = resolveProgressGroupKey(contractProgress.currentStepKey);
    return visibleGroupKeys.includes(candidate) ? candidate : resolveProgressGroupKey(contractProgress.currentStepKey);
  }, [contractProgress.currentStepKey, visibleGroupKeys]);

  const selectedGroupHelperText = useMemo(() => {
    return contractProgress.helperText;
  }, [contractProgress.helperText]);

  const actionGuideVisible =
    nextActionGuide.key !== 'NONE' &&
    nextActionGuide.key !== 'CANCELLED' &&
    nextActionGuide.key !== 'CANCEL_REQUESTED' &&
    nextActionGuide.key !== 'ALL_CLEAR' &&
    nextActionGuide.key !== 'CONTRACT_REJECTED' &&
    nextActionGuide.key !== 'WAIT_CONTRACT_PROGRESS';

  const actionGuideStepKey = useMemo(() => {
    switch (nextActionGuide.key) {
      case 'CONTRACT_REJECTED':
      case 'DECIDE_CONTRACT':
      case 'WAIT_CONTRACT_DECISION':
        return 'REQUEST';
      case 'SIGN_CONTRACT':
      case 'WAIT_CRS_SIGNATURE':
      case 'CREATE_CAMPAIGN':
        return 'SIGNATURE';
      case 'WAIT_CRS_DRAFTING':
      case 'WAIT_CONTENT_REVISION':
        return 'PLAN_WRITE';
      case 'REVIEW_CONTENT_PLAN':
        return 'PLAN_APPROVAL';
      case 'WAIT_URL_SUBMIT':
        return 'CONTENT_REGISTER';
      case 'REVIEW_URL':
      case 'WAIT_URL_RESUBMIT':
        return 'CONTENT_APPROVAL';
      default:
        return null;
    }
  }, [nextActionGuide.key]);

  const selectablePlanReviewItems = useMemo(() => {
    return Array.isArray(contentRows)
      ? contentRows.filter((it) => normalizeContentStatus(it?.status) === 'SUBMITTED')
      : [];
  }, [contentRows]);

  const openContentPlanSelectionModal = React.useCallback(() => {
    setContentPlanSelectionModalOpen(true);
  }, []);

  const closeContentPlanSelectionModal = React.useCallback(() => {
    setContentPlanSelectionModalOpen(false);
  }, []);

  const handleSelectContentPlanReviewItem = React.useCallback((contentItemId) => {
    const numericId = Number(contentItemId);
    if (!Number.isFinite(numericId) || numericId <= 0) return;
    setContentPlanSelectionModalOpen(false);
    void openContentDetail(numericId);
  }, [openContentDetail]);

  const handleCurrentAction = React.useCallback(() => {
    switch (nextActionGuide.key) {
      case 'DECIDE_CONTRACT': {
        if (!canDecide && !canProposeContractCondition) return;
        setError('');
        setDecisionReasonDraft('');
        setDecisionAction('');
        if (hasIncomingConditionProposal) {
          setDecisionProposalCheckConfirmed(false);
          setDecisionProposalCheckOpen(true);
          return;
        }
        setDecisionChoiceModalOpen(true);
        return;
      }
      case 'SIGN_CONTRACT':
        setContractDocReadOnly(false);
        setContractAgreeChecked(false);
        setContractDocOpen(true);
        return;
      case 'CREATE_CAMPAIGN':
        void handleCreateCampaign();
        return;
      case 'REVIEW_CONTENT_PLAN':
        if (actionGuideStepKey === 'PLAN_APPROVAL') {
          if (selectablePlanReviewItems.length === 1) {
            const onlyItemId = Number(selectablePlanReviewItems[0]?.contentItemId);
            if (Number.isFinite(onlyItemId) && onlyItemId > 0) {
              void openContentDetail(onlyItemId);
              return;
            }
          }
          openContentPlanSelectionModal();
          return;
        }
        if (nextActionGuide.contentItemId) {
          void openContentDetail(nextActionGuide.contentItemId);
        }
        return;
      case 'REVIEW_URL':
        if (nextActionGuide.contentItemId) {
          void openContentDetail(nextActionGuide.contentItemId);
        }
        return;
      default:
        return;
    }
  }, [
    actionGuideStepKey,
    nextActionGuide,
    selectablePlanReviewItems,
    openContentPlanSelectionModal,
    canDecide,
    canProposeContractCondition,
    hasIncomingConditionProposal,
    openContentDetail,
    handleCreateCampaign
  ]);

  const resolveTimelineProgressStepKey = React.useCallback((it) => {
    const sc = String(it?.scope || '').trim().toUpperCase();
    const ac = String(it?.action || '').trim().toUpperCase();

    if (sc === 'REQUEST') {
      if (ac === 'REQUESTED' || ac === 'REVIEW') return 'REQUEST';
      if (ac === 'APPROVE' || ac === 'REJECT') return 'APPROVAL';
      return 'REQUEST';
    }
    if (sc === 'CONDITION') return 'APPROVAL';
    if (sc === 'SIGNATURE') return 'SIGNATURE';
    if (sc === 'CONTENT') {
      if (ac === 'REVISION_CREATED') return 'PLAN_WRITE';
      if (ac === 'SUBMIT' || ac === 'APPROVE' || ac === 'REQUEST_REVISION') return 'PLAN_APPROVAL';
      return 'PLAN_WRITE';
    }
    if (sc === 'URL') {
      if (ac === 'SUBMIT') return 'CONTENT_REGISTER';
      if (ac === 'APPROVE' || ac === 'REQUEST_REVISION') return 'CONTENT_APPROVAL';
      return 'CONTENT_REGISTER';
    }
    if (sc === 'CANCEL') {
      if (ac === 'REQUEST') return 'REQUEST';
      return 'APPROVAL';
    }
    return null;
  }, []);

  const shouldShowTimelineMessageToggle = React.useCallback((message) => {
    const normalized = String(message || '').trim();
    if (!normalized) return false;
    if (normalized.includes('\n')) return true;
    return normalized.length > 90;
  }, []);

  const unifiedTimelineItems = useMemo(() => {
    const merged = [...contractTimelineItems, ...contentTimelineItems];
    merged.sort((a, b) => {
      const ta = new Date(String(a?.createdAt || '')).getTime();
      const tb = new Date(String(b?.createdAt || '')).getTime();
      const na = Number.isFinite(ta) ? ta : 0;
      const nb = Number.isFinite(tb) ? tb : 0;
      return na - nb;
    });
    return merged;
  }, [contractTimelineItems, contentTimelineItems]);

  const selectedRows = useMemo(() => {
    const cond = selected?.requestedSalesCondition && typeof selected.requestedSalesCondition === 'object' ? selected.requestedSalesCondition : {};
    const proposed = selected?.proposedSalesCondition && typeof selected.proposedSalesCondition === 'object' ? selected.proposedSalesCondition : {};

    const templateChannels = getApprovalChannels(selected);
    const templateLines = (Array.isArray(templateChannels) ? templateChannels : [])
      .map((ch) => {
        const cfg = getTemplateConfigForChannel(selected, ch);
        const type = String(cfg?.type || '').trim();
        const requiredCount = Number(String((cfg?.requiredCount ?? cfg?.required_count) ?? '').trim() || 0);
        if (!type || !Number.isFinite(requiredCount) || requiredCount <= 0) return '';
        return `${String(ch)}: ${resolveDeliverableLabel(type)} ${requiredCount}개`;
      })
      .filter(Boolean);

    const campaignLines = (Array.isArray(deliverables) ? deliverables : [])
      .map((d) => {
        const type = String(d?.type || '').trim();
        const requiredCount = Number((d?.requiredCount ?? d?.required_count) || 0);
        if (!type || !Number.isFinite(requiredCount) || requiredCount <= 0) return '';
        return `${resolveDeliverableLabel(type)} ${requiredCount}개`;
      })
      .filter(Boolean);

    const deliverableSummary =
      templateLines.length > 0
        ? templateLines.join('\n')
        : campaignLines.length > 0
          ? campaignLines.join('\n')
          : '';

    const renderDiff = (baseValue, proposedValue, proposedIncluded) => {
      const baseComparable = getComparableText(baseValue);
      if (!proposedIncluded) return baseComparable;

      const proposedComparable = getComparableText(proposedValue);
      if (proposedComparable === '-' || proposedComparable === baseComparable) return baseComparable;
      return (
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-red-600 line-through">{baseComparable}</span>
          <span className="text-gray-400">→</span>
          <span className="text-blue-700 font-semibold">{proposedComparable}</span>
        </span>
      );
    };

    const sellStart = cond['sellStartDate'];
    const sellEnd = cond['sellEndDate'];
    const recruitStart = cond['recruitStartDate'];
    const recruitEnd = cond['recruitEndDate'];

    const proposedSellStart = proposed['sellStartDate'];
    const proposedSellEnd = proposed['sellEndDate'];
    const proposedRecruitStart = proposed['recruitStartDate'];
    const proposedRecruitEnd = proposed['recruitEndDate'];

    const sellRange = `${getText(sellStart)}~${getText(sellEnd)}`;
    const proposedSellRange = `${getText(proposedSellStart)}~${getText(proposedSellEnd)}`;
    const recruitRange = `${getText(recruitStart)}~${getText(recruitEnd)}`;
    const proposedRecruitRange = `${getText(proposedRecruitStart)}~${getText(proposedRecruitEnd)}`;

    const sellRangeIncluded = hasOwn(proposed, 'sellStartDate') || hasOwn(proposed, 'sellEndDate');
    const recruitRangeIncluded = hasOwn(proposed, 'recruitStartDate') || hasOwn(proposed, 'recruitEndDate');

    return [
      {
        label: '판매 가능 기간',
        value: renderDiff(sellRange, proposedSellRange, sellRangeIncluded)
      },
      {
        label: '인플루언서 모집기간',
        value: renderDiff(recruitRange, proposedRecruitRange, recruitRangeIncluded)
      },
      {
        label: '추천 타겟 고객층',
        value: renderDiff(cond['targetCustomer'], proposed['targetCustomer'], hasOwn(proposed, 'targetCustomer'))
      },
      {
        label: '판매 수수료 방식',
        value: renderDiff(cond['commissionPolicy'], proposed['commissionPolicy'], hasOwn(proposed, 'commissionPolicy'))
      },
      {
        label: '콘텐츠 제작 지원',
        value: renderDiff(
          cond['contentCreationSupport'],
          proposed['contentCreationSupport'],
          hasOwn(proposed, 'contentCreationSupport')
        )
      },
      {
        label: '상품번호',
        value: getText(selected?.productNo)
      },
      {
        label: '필수 작업물',
        value: renderDiff(deliverableSummary, deliverableSummary, false)
      },
      {
        label: '필수 작업물 제출 기한',
        value: renderDiff(
          cond['deliverableSubmitDeadline'],
          proposed['deliverableSubmitDeadline'],
          hasOwn(proposed, 'deliverableSubmitDeadline')
        )
      },
      {
        label: '협의 가능',
        value: renderDiff(cond['negotiable'] ? '가능' : '불가', cond['negotiable'] ? '가능' : '불가', false)
      }
    ];
  }, [selected, deliverables]);

  const proposedRows = useMemo(() => {
    if (!selected?.proposedSalesCondition) return [];
    const condition = selected.proposedSalesCondition;

    const rows = [
      { key: '판매 가능 시작일(제안)', value: String(condition.sellStartDate || '') },
      { key: '판매 가능 종료일(제안)', value: String(condition.sellEndDate || '') },
      { key: '필수 작업물 제출 기한(제안)', value: String(condition.deliverableSubmitDeadline || '') },
      { key: '판매 수수료 방식(제안)', value: String(condition.commissionPolicy || '') },
      { key: '콘텐츠 제작 지원(제안)', value: String(condition.contentCreationSupport || '') }
    ];

    const meaningful = rows.filter((r) => String(r.value || '').trim().length > 0);
    return meaningful;
  }, [selected]);

  const canRequestCancel =
    selected &&
    ['APPROVED'].includes(String(selected.status || '').toUpperCase()) &&
    ['NONE', 'REJECTED', 'WITHDRAWN'].includes(cancelStatus);

  const cancelActionBlockReason = useMemo(() => {
    if (!selected) return '취소요청이 불가합니다.';
    const st = String(selected.status || '').toUpperCase();
    const cs = String(selected.cancelStatus || 'NONE').toUpperCase();
    if (!['APPROVED'].includes(st)) return '승인 완료 후에만 취소요청이 가능합니다.';
    if (!(cs === 'NONE' || cs === 'REJECTED' || cs === 'WITHDRAWN')) return '이미 취소요청이 진행중입니다.';
    return '';
  }, [selected]);
  const canWithdrawCancel = isCancelRequested && isCancelRequester;
  const canDecideCancel = isCancelRequested && (!isCancelRequester || ALLOW_SELF_CANCEL_DECISION);

  if (loading) {
    return <div className="flex justify-center py-12"><div className="text-sm text-gray-500">로딩 중...</div></div>;
  }

  if (error) {
    return (
      <div className="py-6">
        <div className="text-sm text-red-600">{error}</div>
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            className="px-3 py-2 text-xs rounded border border-gray-300 hover:bg-gray-50"
            onClick={() => navigate('/contracts')}
          >
            목록으로
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Modal
        open={influencerDetailOpen}
        title="인플루언서 상세 (Meta Graph API 샘플)"
        onClose={() => setInfluencerDetailOpen(false)}
        maxWidthClassName="max-w-3xl"
        bodyClassName="overflow-auto"
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="shrink-0">
            <div className="h-20 w-20 rounded-full overflow-hidden bg-gray-100 border border-gray-200">
              {String(influencerDetailGraph?.profile_picture_url || '').trim() ? (
                <img alt="profile" src={String(influencerDetailGraph.profile_picture_url)} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-sm font-bold text-gray-500">
                  {String(influencerDetailGraph?.username || '-').trim().charAt(0) || '-'}
                </div>
              )}
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-lg font-extrabold text-gray-900 truncate">{String(influencerDetailGraph?.name || '-')}</div>
              {String(influencerDetailGraph?.username || '').trim() && (
                <div className="text-sm font-semibold text-indigo-700 truncate">@{String(influencerDetailGraph.username)}</div>
              )}
            </div>
            {String(metaInfluencerProfile?.platform || '').trim() && (
              <div className="mt-0.5 text-xs text-gray-500 truncate">{String(metaInfluencerProfile.platform)}</div>
            )}

            {String(influencerDetailGraph?.biography || '').trim() && (
              <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap break-words">{String(influencerDetailGraph.biography)}</div>
            )}

            <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                <div className="text-[11px] text-gray-500">팔로워</div>
                <div className="text-sm font-bold text-gray-900">{formatCompactNumber(influencerDetailGraph?.followers_count)}</div>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                <div className="text-[11px] text-gray-500">게시물</div>
                <div className="text-sm font-bold text-gray-900">{formatCompactNumber(influencerDetailGraph?.media_count)}</div>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                <div className="text-[11px] text-gray-500">ER</div>
                <div className="text-sm font-bold text-gray-900">{formatPercent(influencerDetailInsights?.engagement_rate, 1)}</div>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                <div className="text-[11px] text-gray-500">평균 리치</div>
                <div className="text-sm font-bold text-gray-900">{formatCompactNumber(influencerDetailInsights?.avg_reach)}</div>
              </div>
            </div>

            <div className="mt-2 grid grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                <div className="text-[11px] text-gray-500">평균 좋아요</div>
                <div className="text-sm font-bold text-gray-900">{formatCompactNumber(influencerDetailInsights?.avg_likes)}</div>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                <div className="text-[11px] text-gray-500">평균 댓글</div>
                <div className="text-sm font-bold text-gray-900">{formatCompactNumber(influencerDetailInsights?.avg_comments)}</div>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                <div className="text-[11px] text-gray-500">평균 저장</div>
                <div className="text-sm font-bold text-gray-900">{formatCompactNumber(influencerDetailInsights?.avg_saves)}</div>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                <div className="text-[11px] text-gray-500">평균 공유</div>
                <div className="text-sm font-bold text-gray-900">{formatCompactNumber(influencerDetailInsights?.avg_shares)}</div>
              </div>
            </div>

            {Array.isArray(influencerDetailTags?.categories) && influencerDetailTags.categories.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {influencerDetailTags.categories.slice(0, 12).map((c) => (
                  <span
                    key={String(c)}
                    className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 px-2 py-1 text-[11px] font-semibold"
                  >
                    {String(c)}
                  </span>
                ))}
              </div>
            )}

            {influencerDetailTopCountries.length > 0 && (
              <div className="mt-3 text-xs text-gray-600">
                <span className="font-semibold text-gray-700">주요 국가</span>
                <span className="ml-2">
                  {influencerDetailTopCountries
                    .slice(0, 5)
                    .map((it) => `${String(it?.country || '')} ${(Number(it?.share) * 100).toFixed(0)}%`)
                    .join(' · ')}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6">
          <div className="text-sm font-bold text-gray-900 mb-2">최근 게시물 (샘플)</div>
          {influencerDetailRecentMedia.length === 0 ? (
            <div className="text-sm text-gray-500">최근 게시물 데이터가 없습니다.</div>
          ) : (
            <div className="space-y-2">
              {influencerDetailRecentMedia.slice(0, 8).map((m) => (
                <a
                  key={String(m?.id || `${String(m?.timestamp || '')}-${String(m?.media_type || '')}`)}
                  href={String(m?.permalink || '#')}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-lg border border-gray-200 p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-gray-700">{String(m?.media_type || '-')}</div>
                      <div className="mt-1 text-sm text-gray-900 font-semibold truncate">{String(m?.caption || '-')}</div>
                    </div>
                    <div className="text-xs text-gray-400 shrink-0">{String(m?.timestamp || '').slice(0, 10)}</div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </Modal>
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <h1 className="text-2xl font-bold text-gray-900 truncate">
              {selected?.productName || '계약조건 상세'}
            </h1>
            <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${
              String(selected?.status || '').toUpperCase() === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' :
              String(selected?.status || '').toUpperCase() === 'REJECTED' ? 'bg-red-50 text-red-700 ring-red-600/20' :
              String(selected?.status || '').toUpperCase() === 'REQUESTED' ? 'bg-indigo-50 text-indigo-700 ring-indigo-600/20' :
              'bg-gray-50 text-gray-600 ring-gray-500/10'
            }`}>
              {STATUS_LABEL[String(selected?.status || '').toUpperCase()] || selected?.status || '-'}
            </span>
            {Number.isFinite(Number(selected?.productNo)) && Number(selected?.productNo) > 0 && (
              <button
                onClick={() => {
                  const productNo = Number(selected?.productNo);
                  if (!Number.isFinite(productNo) || productNo <= 0) return;
                  navigate(`/detail-settings/${productNo}`);
                }}
                className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors"
              >
                상품설정
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
            )}
          </div>

          
        </div>
        
        <div className="flex items-center gap-3 shrink-0 mt-1">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
            onClick={() => navigate(-1)}
          >
            뒤로
          </button>
          {canRequestCancel && (
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-lg border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:bg-gray-400 transition-colors"
              onClick={() => {
                setCancelError('');
                setCancelModalOpen(true);
              }}
              disabled={!selected}
            >
              취소요청
            </button>
          )}
        </div>
      </div>

      {metaInfluencerProfile && (
        <div className="mb-8 w-full rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
            <div className="text-sm font-bold text-gray-900">인플루언서 프로필 (Meta Graph API 샘플)</div>
            <div className="flex items-center gap-3">
              <div className="text-xs text-gray-500">{String(metaInfluencerProfile?.platform || 'INSTAGRAM')}</div>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                onClick={() => setInfluencerDetailOpen(true)}
              >
                자세히보기
              </button>
            </div>
          </div>
          <div className="p-4 flex flex-col sm:flex-row gap-4">
            <div className="shrink-0">
              <div className="h-16 w-16 rounded-full overflow-hidden bg-gray-100 border border-gray-200">
                {String(metaInfluencerProfile?.graph?.profile_picture_url || '').trim() ? (
                  <img
                    alt="profile"
                    src={String(metaInfluencerProfile.graph.profile_picture_url)}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-sm font-bold text-gray-500">
                    {String(metaInfluencerProfile?.graph?.username || '-').trim().charAt(0) || '-'}
                  </div>
                )}
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-base font-bold text-gray-900 truncate">{String(metaInfluencerProfile?.graph?.name || '-')}</div>
                {String(metaInfluencerProfile?.graph?.username || '').trim() && (
                  <div className="text-sm font-semibold text-indigo-700 truncate">@{String(metaInfluencerProfile.graph.username)}</div>
                )}
              </div>
              {String(metaInfluencerProfile?.graph?.biography || '').trim() && (
                <div className="mt-1 text-sm text-gray-600 truncate">{String(metaInfluencerProfile.graph.biography)}</div>
              )}

              <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-2">
                <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                  <div className="text-[11px] text-gray-500">팔로워</div>
                  <div className="text-sm font-bold text-gray-900">{formatCompactNumber(metaInfluencerProfile?.graph?.followers_count)}</div>
                </div>
                <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                  <div className="text-[11px] text-gray-500">ER</div>
                  <div className="text-sm font-bold text-gray-900">{formatPercent(metaInfluencerProfile?.insights?.engagement_rate, 1)}</div>
                </div>
                <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                  <div className="text-[11px] text-gray-500">평균 리치</div>
                  <div className="text-sm font-bold text-gray-900">{formatCompactNumber(metaInfluencerProfile?.insights?.avg_reach)}</div>
                </div>
                <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                  <div className="text-[11px] text-gray-500">평균 좋아요</div>
                  <div className="text-sm font-bold text-gray-900">{formatCompactNumber(metaInfluencerProfile?.insights?.avg_likes)}</div>
                </div>
              </div>

              {Array.isArray(metaInfluencerProfile?.tags?.categories) && metaInfluencerProfile.tags.categories.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {metaInfluencerProfile.tags.categories.slice(0, 6).map((c) => (
                    <span
                      key={String(c)}
                      className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 px-2 py-1 text-[11px] font-semibold"
                    >
                      {String(c)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {decisionProposalCheckOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-md bg-white shadow-lg">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="text-sm font-semibold text-gray-900">제안된 계약조건 확인</div>
              <div className="text-xs text-gray-500 mt-1">상대방이 제안한 변경 내용을 확인한 뒤 다음 단계로 진행하세요.</div>
            </div>

            <div className="px-4 py-4 space-y-3">
              <dl className="grid grid-cols-12 gap-x-2 text-xs">
                <div className="col-span-12 grid grid-cols-12 items-center gap-x-2 py-1">
                  <dt className="col-span-4 text-gray-600" />
                  <dd className="col-span-4 text-gray-500 font-semibold text-center">현재</dd>
                  <dd className="col-span-4 text-indigo-900 font-semibold text-center">제안</dd>
                </div>

                <div className="col-span-12 border-t border-gray-200/70 grid grid-cols-12 items-center gap-x-2 py-2">
                  <dt className="col-span-4 text-gray-600">판매 가능 시작일</dt>
                  <dd className="col-span-4 text-center font-medium text-gray-900">
                    {String(selected?.requestedSalesCondition?.sellStartDate || '-').trim() || '-'}
                  </dd>
                  <dd className="col-span-4 text-center font-semibold text-indigo-900">
                    {String(selected?.proposedSalesCondition?.sellStartDate || '-').trim() || '-'}
                  </dd>
                </div>

                <div className="col-span-12 border-t border-gray-200/70 grid grid-cols-12 items-center gap-x-2 py-2">
                  <dt className="col-span-4 text-gray-600">판매 가능 종료일</dt>
                  <dd className="col-span-4 text-center font-medium text-gray-900">
                    {String(selected?.requestedSalesCondition?.sellEndDate || '-').trim() || '-'}
                  </dd>
                  <dd className="col-span-4 text-center font-semibold text-indigo-900">
                    {String(selected?.proposedSalesCondition?.sellEndDate || '-').trim() || '-'}
                  </dd>
                </div>

                <div className="col-span-12 border-t border-gray-200/70 grid grid-cols-12 items-center gap-x-2 py-2">
                  <dt className="col-span-4 text-gray-600">필수 작업물 제출 기한</dt>
                  <dd className="col-span-4 text-center font-medium text-gray-900">
                    {String(selected?.requestedSalesCondition?.deliverableSubmitDeadline || '-').trim() || '-'}
                  </dd>
                  <dd className="col-span-4 text-center font-semibold text-indigo-900">
                    {String(selected?.proposedSalesCondition?.deliverableSubmitDeadline || '-').trim() || '-'}
                  </dd>
                </div>

                <div className="col-span-12 border-t border-gray-200/70 grid grid-cols-12 items-center gap-x-2 py-2">
                  <dt className="col-span-4 text-gray-600">판매 수수료 방식</dt>
                  <dd className="col-span-4 text-center font-medium text-gray-900">
                    {String(selected?.requestedSalesCondition?.commissionPolicy || '-').trim() || '-'}
                  </dd>
                  <dd className="col-span-4 text-center font-semibold text-indigo-900">
                    {String(selected?.proposedSalesCondition?.commissionPolicy || '-').trim() || '-'}
                  </dd>
                </div>

                <div className="col-span-12 border-t border-gray-200/70 grid grid-cols-12 items-center gap-x-2 py-2">
                  <dt className="col-span-4 text-gray-600">콘텐츠 제작 지원</dt>
                  <dd className="col-span-4 text-center font-medium text-gray-900">
                    {String(selected?.requestedSalesCondition?.contentCreationSupport || '-').trim() || '-'}
                  </dd>
                  <dd className="col-span-4 text-center font-semibold text-indigo-900">
                    {String(selected?.proposedSalesCondition?.contentCreationSupport || '-').trim() || '-'}
                  </dd>
                </div>
              </dl>

              <label className="flex items-start gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={decisionProposalCheckConfirmed}
                  onChange={(e) => setDecisionProposalCheckConfirmed(e.target.checked)}
                />
                <span>상대방이 제안한 계약조건 변경사항을 확인했습니다.</span>
              </label>
            </div>

            <div className="px-4 py-3 border-t border-gray-200 flex gap-2 justify-end">
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                disabled={!!savingAction}
                onClick={() => {
                  setDecisionProposalCheckOpen(false);
                  setDecisionProposalCheckConfirmed(false);
                  setDecisionAction('');
                }}
              >
                닫기
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={!!savingAction || !decisionProposalCheckConfirmed}
                onClick={() => {
                  setDecisionProposalCheckOpen(false);
                  setDecisionChoiceModalOpen(true);
                }}
              >
                다음
              </button>
            </div>
          </div>
        </div>
      )}

      {decisionStageVisible &&
        decisionBlockReason &&
        nextActionGuide?.key !== 'WAIT_CONTRACT_DECISION' && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
            <div className="shrink-0 w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center mt-0.5">
              <svg className="w-3 h-3 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="text-sm font-medium text-amber-800 pt-0.5">{decisionBlockReason}</div>
          </div>
        )}

      {isCancelApproved && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
          <div className="shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
            <svg className="w-3 h-3 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <div>
            <div className="text-sm font-bold text-red-800">취소된 계약입니다.</div>
            <div className="mt-1 text-sm text-red-700">콘텐츠 등록 및 조회가 불가능합니다.</div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-4">
            <ContractProgressNavigator
              currentStepKey={contractProgress.currentStepKey}
              nextStepKey={contractProgress.nextStepKey}
              helperText={selectedGroupHelperText}
              selectedGroupKey={currentProgressGroupKey}
              onSelectGroup={() => {}}
              actionStepKey={actionGuideVisible ? actionGuideStepKey : null}
              actionButtonLabel={actionGuideButtonLabel}
              onActionClick={actionGuideVisible ? handleCurrentAction : undefined}
              topRightSlot={(
                <button
                  type="button"
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  onClick={() => setTimelineTableOpen((prev) => !prev)}
                >
                  {timelineTableOpen ? '진행이력 닫기' : '진행이력 보기'}
                </button>
              )}
              visibleStepKeys={contractProgress.visibleStepKeys}
              currentStepTitle={contractProgress.currentStepTitle}
              stepLabelOverrides={contractProgress.stepLabelOverrides}
              tone={contractProgress.tone}
            />
          </div>

          {timelineTableOpen && (
            <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/40">
              <>
                {timelineState.error && <div className="mt-3 text-xs text-red-600">{timelineState.error}</div>}
                {timelineState.loading ? (
                  <div className="mt-4 text-sm text-gray-500">진행 이력을 불러오는 중입니다.</div>
                ) : unifiedTimelineItems.length === 0 ? (
                  <div className="mt-4 flex items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white px-4 py-10 text-sm text-gray-500">
                    진행 이력이 아직 없습니다.
                  </div>
                ) : (
                  <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200 bg-white">
                    <div className="max-h-[420px] overflow-y-auto">
                      <table className="min-w-full border-collapse">
                        <thead className="sticky top-0 z-10 bg-gray-50">
                          <tr className="text-xs font-semibold text-gray-500">
                            <th className="px-3 py-3 text-left border-b border-gray-200">구분</th>
                            <th className="px-3 py-3 text-left border-b border-gray-200">처리</th>
                            <th className="px-3 py-3 text-left border-b border-gray-200 w-[55%]">코멘트</th>
                            <th className="px-3 py-3 text-left border-b border-gray-200">작성자</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {unifiedTimelineItems.map((it, idx) => {
                            const sc = String(it?.scope || '').trim().toUpperCase();
                            const ac = String(it?.action || '').trim().toUpperCase();
                            const messageRaw = String(it?.message || '').trim();
                            const decodedMessage = decodeHtmlEntities(messageRaw);
                            const isConditionProposal = sc === 'CONDITION' && ac === 'PROPOSE';
                            const isSignature = sc === 'SIGNATURE' && ac === 'SIGN';
                            const showDetailButton = isConditionProposal || isSignature;
                            const canToggle = shouldShowTimelineMessageToggle(decodedMessage);
                            const visibleMessage = canToggle ? `${decodedMessage.slice(0, 160)}${decodedMessage.length > 160 ? '…' : ''}` : decodedMessage;

                            return (
                              <tr key={getTimelineReactKey(it, idx)} className="text-sm text-gray-900">
                                <td className="px-3 py-3 align-top">
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="font-semibold">{resolveTimelineLabel(it.scope, it.action)}</div>
                                      {typeof it.contentItemId === 'number' ? (
                                        <div className="mt-0.5 text-xs text-gray-400">콘텐츠 #{it.contentItemId}</div>
                                      ) : null}
                                    </div>
                                    {showDetailButton ? (
                                      <button
                                        type="button"
                                        className="shrink-0 inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                                        onClick={() => {
                                          if (isConditionProposal) {
                                            openConditionHistoryModal(it);
                                            return;
                                          }
                                          if (isSignature) {
                                            openContractDocPreview();
                                          }
                                        }}
                                      >
                                        {isConditionProposal ? '상세 보기' : '계약서 보기'}
                                      </button>
                                    ) : null}
                                  </div>
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap align-middle text-gray-700">{formatTimelineDateTime(it.createdAt)}</td>
                                <td className="px-3 py-3 align-middle w-[55%]">
                                  {decodedMessage ? (
                                    <div className="whitespace-pre-wrap break-words leading-6">
                                      {visibleMessage}
                                      {canToggle && (
                                        <div className="mt-2">
                                          <button
                                            type="button"
                                            className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                                            onClick={() => openTimelineCommentModal(it)}
                                          >
                                            더보기
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap align-middle text-gray-700">{resolveActorLabel(it.actorSide)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-gray-100 px-5 py-3 bg-white">
            <nav className="flex gap-2" aria-label="계약관리 상세 탭">
              <button
                type="button"
                className={`px-3 py-2 text-sm font-semibold rounded-lg border transition-colors ${
                  activeTab === 'CONDITION'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
                onClick={() => setActiveTab('CONDITION')}
                aria-current={activeTab === 'CONDITION' ? 'page' : undefined}
              >
                캠페인 조건
              </button>
              <button
                type="button"
                className={`px-3 py-2 text-sm font-semibold rounded-lg border transition-colors ${
                  activeTab === 'CONTENT'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
                onClick={() => setActiveTab('CONTENT')}
                aria-current={activeTab === 'CONTENT' ? 'page' : undefined}
              >
                콘텐츠 등록
              </button>
            </nav>
          </div>

          <div className="grid grid-cols-1 gap-0 divide-y divide-gray-200">
            {activeTab === 'CONDITION' && (
              <div className="min-h-0 bg-white">
                <div className="px-5 py-4 border-b border-gray-100/80">
                  <h3 className="text-base font-bold text-gray-900">계약조건</h3>
                </div>
                <div className="px-5 py-2">
                  {selectedRows
                    .filter((r) => !String(r.key || '').includes('(제안)'))
                    .map((r) => (
                      <div key={r.label}>
                        <ConditionFieldRow label={r.label} value={r.value} />
                      </div>
                    ))}
                </div>

                <div className="border-t border-gray-200">
                  <div className="px-5 py-4 border-b border-gray-100/80 bg-white">
                    <h3 className="text-base font-bold text-gray-900">캠페인 안내</h3>
                  </div>
                  <div className="px-5 py-5 bg-gray-50/50">
                    <HtmlPreview html={String(selected?.requestedSalesCondition?.note || selected?.requestNote || '')} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'CONTENT' && (
              <div className="min-h-0 bg-white">
                <div className="px-5 py-4 border-b border-gray-100/80">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-base font-bold text-gray-900">캠페인/콘텐츠</h3>
                    {campaignState.loading && <div className="text-xs text-gray-500">로딩 중...</div>}
                  </div>
                </div>

                <div className="px-5 py-5">
                  {campaignState.error && <div className="text-xs text-red-600 mb-2">{campaignState.error}</div>}
                  {contentError && <div className="text-xs text-red-600 mb-2">{contentError}</div>}

                  {!canCreateCampaign ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                      </div>
                      <div className="text-sm text-gray-600 font-medium">
                        캠페인 콘텐츠는 계약서 양측 서명 완료 후 작성할 수 있습니다.
                      </div>
                    </div>
                  ) : !campaign ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <div className="text-sm text-gray-600 mb-3">
                        계약서 서명이 완료되었습니다. 캠페인을 생성한 뒤 콘텐츠를 등록할 수 있습니다.
                      </div>
                      <button
                        type="button"
                        className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
                        disabled={!!savingAction}
                        onClick={() => void handleCreateCampaign()}
                      >
                        {savingAction === 'CREATE_CAMPAIGN' ? '생성 중...' : '캠페인 생성하기'}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {deliverables.length === 0 ? (
                        <div className="text-sm text-gray-500 text-center py-8">필수 작업물(Deliverables)을 먼저 설정하세요.</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full border-collapse">
                            <thead>
                              <tr className="text-xs font-semibold text-gray-500 bg-gray-50">
                                <th className="px-3 py-2 text-center border-b border-gray-200">채널</th>
                                <th className="px-3 py-2 text-left border-b border-gray-200">작업물</th>
                                <th className="px-3 py-2 text-left border-b border-gray-200">URL</th>
                                <th className="px-3 py-2 text-left border-b border-gray-200">기획안 상태</th>
                                <th className="px-3 py-2 text-left border-b border-gray-200">콘텐츠 등록 상태</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {deliverableSlots.map((slot) => {
                                const label = `${String(slot.type || '-')}`;
                                const slotNo = Number(slot.slotIndex) + 1;
                                const rawType = String(slot.type || '').trim().toUpperCase();
                                const shortType = rawType
                                  .replace(/^YOUTUBE_/, '')
                                  .replace(/^INSTAGRAM_/, '')
                                  .replace(/^NAVER_/, '')
                                  .replace(/^TIKTOK_/, '')
                                  .replace(/^X_/, '')
                                  .replace(/^BLOG_/, '')
                                  .trim();
                                const typeNoLabel = ['REELS', 'FEED', 'SHORTS'].includes(shortType)
                                  ? `${shortType}${slotNo}`
                                  : `${shortType || label} ${slotNo}`;
                                const it = slot.item;

                                const publishedUrl = it ? String(it.publishedUrl || '').trim() : '';
                                const urlLabel = it ? (publishedUrl ? 'URL 등록' : 'URL 미등록') : '-';

                                const canOpenDetail = Boolean(it) && !isCancelApproved;

                                return (
                                  <tr
                                    key={it ? String(it.contentItemId) : `${slot.deliverableId}-${slot.slotIndex}`}
                                    className={`text-sm text-gray-900 ${canOpenDetail ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                                    role={canOpenDetail ? 'button' : undefined}
                                    tabIndex={canOpenDetail ? 0 : undefined}
                                    onClick={() => {
                                      if (!canOpenDetail) return;
                                      void openContentDetail(it.contentItemId);
                                    }}
                                    onKeyDown={(e) => {
                                      if (!canOpenDetail) return;
                                      if (e.key !== 'Enter') return;
                                      void openContentDetail(it.contentItemId);
                                    }}
                                  >
                                    <td className="px-3 py-3 whitespace-nowrap">
                                      <span className="inline-flex items-center justify-center w-10" aria-hidden="true">
                                        {getChannelIconBadge(slot.type)}
                                      </span>
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap font-semibold">{typeNoLabel}</td>
                                    <td className="px-3 py-3 whitespace-nowrap">
                                      {it && publishedUrl ? (
                                        <a
                                          href={publishedUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:underline font-semibold"
                                        >
                                          {urlLabel}
                                        </a>
                                      ) : (
                                        <span className={it ? (publishedUrl ? 'text-blue-600 font-semibold' : 'text-gray-400 font-semibold') : 'text-gray-400'}>
                                          {urlLabel}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-3">
                                      {it ? (
                                        <ContentStatusBadge status={it.status} />
                                      ) : (
                                        <span className="text-gray-600 font-medium">기획안 작성 대기</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-3">
                                      {it ? <UrlStatusBadge status={it.urlStatus} /> : <span className="text-gray-400">-</span>}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      <Modal
        open={contractDocOpen}
        title="계약서"
        maxWidthClassName="max-w-4xl"
        onClose={() => {
          if (!!savingAction) return;
          setContractDocOpen(false);
          setContractAgreeChecked(false);
          setContractDocReadOnly(false);
        }}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-gray-600">승인된 계약 기준으로 자동 생성된 계약서입니다.</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                onClick={() => handlePrintContract()}
                disabled={!!savingAction}
              >
                출력
              </button>
            </div>
          </div>

          <div ref={contractDocRef} className="rounded border border-gray-200 bg-white p-4 text-[12px] text-gray-900 leading-6">
            <div className="text-center text-sm font-semibold mb-4">여행상품 판매 및 콘텐츠 제작 계약서</div>

            <div className="space-y-3">
              <div>
                <div className="font-semibold">제1조 (목적)</div>
                <div>
                  본 계약은 여행상품을 공급하는 <span className="font-semibold">공급사</span>와 SNS 채널을 통해 해당 상품을 홍보 및 판매하는 <span className="font-semibold">인플루언서</span> 간의 여행상품 판매 활동 및 콘텐츠 제작에 관한 권리·의무를 규정함을 목적으로 한다.
                </div>
              </div>

              <div>
                <div className="font-semibold">제2조 (계약 대상 상품)</div>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full border-collapse text-[12px]">
                    <tbody>
                      <tr>
                        <th className="border border-gray-200 bg-gray-50 px-2 py-1 text-left w-32">상품명</th>
                        <td className="border border-gray-200 px-2 py-1">{String(selected?.productName || '-')}</td>
                      </tr>
                      <tr>
                        <th className="border border-gray-200 bg-gray-50 px-2 py-1 text-left">상품 판매페이지</th>
                        <td className="border border-gray-200 px-2 py-1">플랫폼 내 등록 상품 기준</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div className="font-semibold">제3조 (판매 가능 기간)</div>
                <div className="mt-1">판매 시작일 : {String(selected?.requestedSalesCondition?.sellStartDate || '-')}</div>
                <div>판매 종료일 : {String(selected?.requestedSalesCondition?.sellEndDate || '-')}</div>
                <div className="mt-1">상기 판매 가능 기간 내 발생한 예약 건에 대해서만 본 계약에 따른 판매 수수료가 적용된다.</div>
              </div>

              <div>
                <div className="font-semibold">제4조 (판매 수수료 및 정산 방식)</div>
                <div className="mt-1">수수료 방식 : {String(selected?.requestedSalesCondition?.commissionPolicy || '-')}</div>
                <div className="mt-1">수수료 산정 기준은 실제 결제 완료된 금액을 기준으로 한다.</div>
                <div>취소 및 환불 발생 시 해당 예약 건에 대한 수수료는 지급하지 않는다.</div>
              </div>

              <div>
                <div className="font-semibold">제5조 (콘텐츠 제작 지원)</div>
                <div className="mt-1 whitespace-pre-wrap break-words">{selected?.requestedSalesCondition?.contentCreationSupport || '-'}</div>
              </div>

              <div>
                <div className="font-semibold">제6조 (필수 작업물)</div>
                <div className="mt-1 whitespace-pre-wrap break-words">{contractDeliverableSummary || '-'}</div>
                <div className="mt-1">판매 링크 삽입 여부 : 필수</div>
              </div>

              <div>
                <div className="font-semibold">제7조 (필수 작업물 제출 기한)</div>
                <div className="mt-1">업로드 완료 기한 : {String(selected?.requestedSalesCondition?.deliverableSubmitDeadline || '-')}</div>
                <div className="mt-1">작업물 업로드 완료 후 결과 URL을 플랫폼 또는 공급사에 제출하여야 한다.</div>
              </div>

              <div>
                <div className="font-semibold">제8조 (계약 기간)</div>
                <div className="mt-1">본 계약의 유효기간은 계약 체결일로부터 판매 종료일까지로 한다.</div>
              </div>

              <div>
                <div className="font-semibold">제9조 (계약 해지)</div>
                <div className="mt-1">본 계약상의 의무를 중대하게 위반한 경우</div>
                <div>허위 또는 과장 광고가 발생한 경우</div>
                <div>기타 계약의 목적 달성이 어려운 사유가 발생한 경우</div>
              </div>

              <div>
                <div className="font-semibold">제10조 (기타)</div>
                <div className="mt-1">본 계약에 명시되지 아니한 사항은 관계 법령 및 상호 협의에 따른다.</div>
              </div>

              <div>
                <div className="font-semibold">제11조 (분쟁 해결)</div>
                <div className="mt-1">본 계약과 관련하여 발생하는 분쟁은 공급사의 소재지를 관할하는 법원을 전속 관할 법원으로 한다.</div>
              </div>

              <div className="pt-3 border-t border-gray-200">
                <div className="font-semibold">(서명란)</div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded border border-gray-200 p-3">
                    <div className="font-semibold">공급사</div>
                    <div className="mt-1">회사명 : {String(contractSupplierInfo?.companyName || '-')}</div>
                    <div>사업자번호 : {String(contractSupplierInfo?.businessNo || '-')}</div>
                    <div>대표자명 : {String(contractSupplierInfo?.ceoName || '-')}</div>
                    <div>서명 : {String(selected?.trpSignedAt || '').trim() ? `완료 (${formatTimelineDateTime(selected.trpSignedAt)})` : '-'}</div>
                  </div>
                  <div className="rounded border border-gray-200 p-3">
                    <div className="font-semibold">인플루언서</div>
                    <div className="mt-1">한글이름 : {String(pickFirstNonEmpty(contractInfluencerInfo, ['유저명', '한글명', '성명']) || selected?.requestUserName || selected?.requestCompanyName || '-')}</div>
                    <div>휴대폰번호 : {String(pickFirstNonEmpty(contractInfluencerInfo, ['대표전화', '휴대폰', '휴대폰번호', '전화번호', '연락처']) || '-')}</div>
                    <div>생년월일 : {String((pickFirstNonEmpty(contractInfluencerInfo, ['생년월일', '생년월일(양력)', '생일']) || '').slice(0, 10) || '-')}</div>
                    <div>서명 : {String(selected?.crsSignedAt || '').trim() ? `완료 (${formatTimelineDateTime(selected.crsSignedAt)})` : '-'}</div>
                  </div>
                </div>
                <div className="mt-2">계약 체결일 : {formatTimelineDateTime(selected?.approvedAt)}</div>
              </div>
            </div>
          </div>

          {String(contractStatus || '').toUpperCase() === 'APPROVED' && !isCancelApproved && !contractDocReadOnly && (
            <div className="rounded border border-gray-200 bg-gray-50 p-3 space-y-2">
              <label className="flex items-center gap-2 text-xs text-gray-800">
                <input
                  type="checkbox"
                  checked={contractAgreeChecked}
                  onChange={(e) => setContractAgreeChecked(Boolean(e.target.checked))}
                  disabled={!!savingAction}
                />
                계약서 내용을 확인했으며 서명(동의)합니다.
              </label>
              <div className="flex justify-end">
                <button
                  type="button"
                  className="px-3 py-2 text-xs rounded bg-slate-900 text-white disabled:bg-gray-400"
                  onClick={() => void handleSignContract()}
                  disabled={!!savingAction || !contractAgreeChecked || Boolean(String(selected?.trpSignedAt || '').trim())}
                >
                  {String(selected?.trpSignedAt || '').trim() ? '서명 완료' : savingAction === 'SIGN' ? '서명 중...' : '서명(동의)'}
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={timelineCommentModalOpen}
        title={timelineCommentModalTitle || '코멘트 전체보기'}
        maxWidthClassName="max-w-2xl"
        onClose={() => closeTimelineCommentModal()}
      >
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span className="inline-flex items-center rounded-full border border-gray-200 px-2 py-0.5 font-medium text-gray-700">
            {resolveActorLabel(timelineCommentModalActorSide)}
          </span>
          <span>{formatTimelineDateTime(timelineCommentModalCreatedAt)}</span>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap break-words leading-6">
          {decodeHtmlEntities(timelineCommentModalMessage)}
        </div>
      </Modal>

      <Modal
        open={alertModalOpen}
        title="알림"
        onClose={() => {
          setAlertModalOpen(false);
          setAlertMessage('');
        }}
      >
        <div className="text-sm text-gray-800">{alertMessage}</div>
      </Modal>

      <Modal
        open={contentPlanSelectionModalOpen}
        title="승인할 콘텐츠 기획안 선택"
        maxWidthClassName="max-w-2xl"
        onClose={closeContentPlanSelectionModal}
      >
        <div className="space-y-4">
        
          {selectablePlanReviewItems.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
              승인 대기 중인 콘텐츠 기획안이 없습니다.
            </div>
          ) : (
            <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
              {selectablePlanReviewItems.map((it) => {
                const contentItemId = Number(it?.contentItemId);
                const deliverableLabel = resolveDeliverableLabel(it?.deliverableType);
                const contentTitle = String(it?.title || '').trim();
                const revisionRound = Number(it?.revisionRound);
                return (
                  <button
                    key={`plan-review-select-${contentItemId}`}
                    type="button"
                    className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-4 text-left shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50/40"
                    onClick={() => handleSelectContentPlanReviewItem(contentItemId)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900">
                          {contentTitle || `콘텐츠 #${contentItemId}`}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                          <span>콘텐츠ID #{contentItemId}</span>
                          {deliverableLabel ? <span>| {deliverableLabel}</span> : null}
                          {Number.isFinite(revisionRound) && revisionRound > 0 ? <span>| {revisionRound}차 기획안</span> : null}
                        </div>
                      </div>
                      <ContentStatusBadge status={it?.status} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Modal>

      {cancelModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (cancelSaving) return;
              setCancelModalOpen(false);
            }}
          />
          <div className="relative w-full max-w-lg rounded-lg bg-white shadow-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900">취소요청</div>
              <button
                type="button"
                className="text-sm text-gray-500 hover:text-gray-700"
                onClick={() => {
                  if (cancelSaving) return;
                  setCancelModalOpen(false);
                }}
              >
                닫기
              </button>
            </div>
            <div className="px-4 py-4 space-y-3">
              {!canRequestCancel && cancelActionBlockReason && (
                <div className="text-xs text-gray-600">{cancelActionBlockReason}</div>
              )}
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
                rows={4}
                placeholder="취소 사유(필수)"
                disabled={cancelSaving || !canRequestCancel}
              />
              {cancelError && <div className="text-xs text-red-600">{cancelError}</div>}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="px-3 py-2 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => setCancelModalOpen(false)}
                  disabled={cancelSaving}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="px-3 py-2 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400"
                  onClick={() => void handleCancelRequest()}
                  disabled={cancelSaving || !canRequestCancel || String(cancelReason || '').trim().length === 0}
                >
                  {cancelSaving ? '요청 중...' : '취소요청 보내기'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={contentDetailOpen}
        title="콘텐츠 검토"
        maxWidthClassName="max-w-5xl"
        maxHeightClassName="h-[90vh]"
        bodyClassName="flex flex-col overflow-hidden"
        onClose={() => {
          if (contentDetailLoading || !!savingAction) return;
          setContentDetailOpen(false);
          setContentDetail(null);
          setContentReviewNote('');
          setUrlReviewNote('');
          setUrlRejectAlsoRequestCopyRevision(false);
        }}
      >
        {contentDetail?.error ? (
          <div className="text-sm text-red-600">{String(contentDetail.error)}</div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[3fr_1fr] gap-3 min-w-0">
            <div className="space-y-2 min-w-0 overflow-y-auto min-h-0 pr-1">
              <div className="text-xs text-gray-700">
                콘텐츠ID: <span className="font-semibold">{contentDetail?.item?.contentItemId}</span>
              </div>
              <div className="text-xs text-gray-700">상태: {String(contentDetail?.item?.status || '')}</div>

              {(() => {
                const url = String(contentDetail?.item?.publishedUrl || '').trim();
                const hasPublishedUrl = Boolean(url);

                const latest =
                  Array.isArray(contentDetail?.revisions) && contentDetail.revisions.length > 0 ? contentDetail.revisions[0] : null;
                const payload = latest?.payload && typeof latest.payload === 'object' ? latest.payload : {};
                const text = String(payload?.text || '');
                const normalizedText = decodeHtmlEntities(text);
                if (!normalizedText.trim() && !hasPublishedUrl) return null;

                if (hasPublishedUrl && !contentShowPlan) {
                  const embedUrl = toEmbedUrl(url);
                  return (
                    <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 flex flex-col">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[11px] font-semibold text-gray-600">게시물 미리보기</div>
                        <button
                          type="button"
                          className="text-[11px] text-gray-600 hover:underline"
                          onClick={() => setContentShowPlan(true)}
                        >
                          기획안 보기
                        </button>
                      </div>
                      <div className="mt-2 flex-1 min-h-0 rounded-md border border-gray-300 overflow-hidden bg-white">
                        <iframe
                          title="PublishedUrlEmbed"
                          src={embedUrl || url}
                          className="w-full h-full"
                          style={{ border: 0, minHeight: 420 }}
                          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                      </div>
                    </div>
                  );
                }

                const looksLikeHtml = /<[^>]+>/.test(text);
                return (
                  <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[11px] font-semibold text-gray-600">최신 콘텐츠 기획안</div>
                      {hasPublishedUrl && (
                        <button
                          type="button"
                          className="text-[11px] text-gray-600 hover:underline"
                          onClick={() => setContentShowPlan(false)}
                        >
                          접기
                        </button>
                      )}
                    </div>
                    <div className="mt-1">
                      {looksLikeHtml ? <HtmlPreview html={text} /> : <div className="text-xs text-gray-900 whitespace-pre-wrap break-words">{normalizedText}</div>}
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const urlStatus = String(contentDetail?.item?.urlStatus || '').toUpperCase();
                if (!urlStatus) return null;
                if (urlStatus !== 'SUBMITTED') return null;
                return (
                  <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="text-[11px] font-semibold text-gray-600">URL 검토</div>
                    <textarea
                      className="mt-2 w-full border rounded px-3 py-2 text-sm"
                      rows={3}
                      value={urlReviewNote}
                      onChange={(e) => setUrlReviewNote(e.target.value)}
                      placeholder="URL 승인/반려 메모(선택)"
                      disabled={!!savingAction}
                    />
                    <label className="mt-2 inline-flex items-center gap-2 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={urlRejectAlsoRequestCopyRevision}
                        onChange={(e) => setUrlRejectAlsoRequestCopyRevision(e.target.checked)}
                        disabled={!!savingAction}
                      />
                      URL 반려 시 콘텐츠 기획안도 함께 수정요청(옵션)
                    </label>
                  </div>
                );
              })()}

              {(() => {
                const contentStage = String(contentDetail?.item?.status || '').toUpperCase() === 'SUBMITTED';
                const urlStage = !contentStage && String(contentDetail?.item?.urlStatus || '').toUpperCase() === 'SUBMITTED';

                return (
                  <div className="flex gap-2 justify-end">
                    {contentStage && (
                      <>
                        <button
                          type="button"
                          className="px-3 py-1.5 text-xs rounded bg-emerald-600 text-white disabled:opacity-50"
                          disabled={!!savingAction}
                          onClick={() => void handleContentReview('APPROVE')}
                        >
                          {savingAction === 'APPROVE' ? '처리 중...' : '승인'}
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1.5 text-xs rounded bg-red-600 text-white disabled:opacity-50"
                          disabled={!!savingAction}
                          onClick={() => openContentRevisionRequestModal()}
                        >
                          {savingAction === 'REQUEST_REVISION' ? '처리 중...' : '수정요청'}
                        </button>
                      </>
                    )}

                    {urlStage && (
                      <>
                        <button
                          type="button"
                          className="px-3 py-1.5 text-xs rounded bg-slate-900 text-white disabled:opacity-50"
                          disabled={!!savingAction}
                          onClick={() => void handleUrlReview('APPROVE')}
                        >
                          {savingAction === 'URL_APPROVE' ? '처리 중...' : 'URL 승인'}
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1.5 text-xs rounded bg-slate-700 text-white disabled:opacity-50"
                          disabled={!!savingAction}
                          onClick={() => void handleUrlReview('REQUEST_REVISION')}
                        >
                          {savingAction === 'URL_REQUEST_REVISION' ? '처리 중...' : 'URL 반려'}
                        </button>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="rounded border border-gray-200 bg-white px-3 py-2 flex flex-col min-h-0 self-stretch min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-semibold text-gray-700">콘텐츠 기획 히스토리</div>
                {timelineState?.loading && <div className="text-[11px] text-gray-500">로딩 중...</div>}
              </div>

              {(() => {
                const id = Number(contentDetail?.item?.contentItemId);
                const all = Array.isArray(timelineState?.items) ? timelineState.items : [];
                const rawItems = all.filter((it) => Number(it?.contentItemId) === id);
                const seenKeys = new Set();
                const items = [];

                const normalizeCreatedAtForKey = (v) => {
                  const s = String(v || '').trim();
                  if (!s) return '';
                  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return s.slice(0, 16);
                  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) return s.slice(0, 16);
                  return s;
                };

                const normalizeMessageForKey = (v) => {
                  return String(v || '')
                    .replace(/\s+/g, ' ')
                    .trim();
                };

                const revisionCreatedKeys = new Set();
                for (const it of rawItems) {
                  const sc = String(it?.scope || '').trim().toUpperCase();
                  const ac = String(it?.action || '').trim().toUpperCase();
                  if (!(sc === 'CONTENT' && ac === 'REVISION_CREATED')) continue;
                  const actorSide = String(it?.actorSide || '').trim().toUpperCase();
                  const createdAt = normalizeCreatedAtForKey(it?.createdAt);
                  const contentRevisionId = typeof it?.contentRevisionId === 'number' ? String(it.contentRevisionId) : String(it?.contentRevisionId || '');
                  revisionCreatedKeys.add([actorSide, createdAt, contentRevisionId].join('|'));
                }

                for (const it of rawItems) {
                  const commentId = it?.commentId;
                  const keyByCommentId = typeof commentId === 'number' && Number.isFinite(commentId) ? `c:${commentId}` : '';
                  const sc = String(it?.scope || '').trim().toUpperCase();
                  const ac = String(it?.action || '').trim().toUpperCase();
                  const actorSide = String(it?.actorSide || '').trim().toUpperCase();
                  const createdAt = normalizeCreatedAtForKey(it?.createdAt);
                  const contentRevisionId = typeof it?.contentRevisionId === 'number' ? String(it.contentRevisionId) : String(it?.contentRevisionId || '');
                  const msg = normalizeMessageForKey(it?.message);

                  if (sc === 'CONTENT' && ac === 'SUBMIT') {
                    const matchKey = [actorSide, createdAt, contentRevisionId].join('|');
                    if (revisionCreatedKeys.has(matchKey)) continue;
                  }

                  const fallbackKey = [sc, ac, actorSide, createdAt, contentRevisionId, msg].join('|');
                  const key = keyByCommentId || fallbackKey;
                  if (seenKeys.has(key)) continue;
                  seenKeys.add(key);
                  items.push(it);
                }

                if (!Number.isFinite(id) || id <= 0) {
                  return <div className="mt-2 text-[11px] text-gray-500">표시할 이력이 없습니다.</div>;
                }
                if (items.length === 0) {
                  return <div className="mt-2 text-[11px] text-gray-500">아직 제출한 기획안이 없습니다</div>;
                }

                return (
                  <div className="mt-2 flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1">
                    <div className="space-y-3">
                      {items.map((it, idx) => {
                        const actorSide = String(it.actorSide || '');
                        const isFirst = idx === 0;
                        const isLast = idx === items.length - 1;
                        const dotClass =
                          actorSide === 'TRP'
                            ? 'bg-purple-600'
                            : actorSide === 'CRS'
                              ? 'bg-blue-600'
                              : actorSide === 'SYSTEM'
                                ? 'bg-gray-500'
                                : 'bg-slate-600';

                        return (
                          <div key={String(it.commentId ?? `${it.kind}-${idx}`)} className="grid grid-cols-[24px_1fr] gap-0">
                            <div className="relative flex justify-center self-stretch">
                              {!isFirst && (
                                <div className="absolute left-1/2 -top-3 bottom-1/2 -translate-x-1/2 w-px bg-gray-200 z-0" />
                              )}
                              <div
                                className={`absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-white z-10 ${dotClass}`}
                              />
                              {!isLast && (
                                <div className="absolute left-1/2 top-1/2 -bottom-3 -translate-x-1/2 w-px bg-gray-200 z-0" />
                              )}
                            </div>
                            <div className="rounded border border-gray-200 bg-white px-3 py-2 min-w-0">
                              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                                <div className="text-[11px] text-gray-600">{formatTimelineDateTime(it.createdAt)}</div>
                                <div className="text-[11px] text-gray-700 font-semibold">{resolveActorLabel(it.actorSide)}</div>
                              </div>
                              <div className="mt-1 text-[11px] text-gray-900 font-semibold">{resolveTimelineLabel(it.scope, it.action)}</div>
                              {String(it.message || '').trim() && (
                                <div className="mt-1 text-[11px] text-gray-700 whitespace-pre-wrap break-words">{it.message}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </Modal>

      {contentRevisionRequestModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-md bg-white shadow-lg">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="text-sm font-semibold text-gray-900">콘텐츠 수정요청</div>
              <div className="text-xs text-gray-500 mt-1">수정요청 사유를 입력한 후 저장하세요.</div>
            </div>

            <div className="px-4 py-3 space-y-2">
              {error && <div className="text-sm text-red-600">{error}</div>}
              <textarea
                className="w-full border rounded px-3 py-2 text-sm"
                rows={4}
                value={contentRevisionRequestNoteDraft}
                onChange={(e) => setContentRevisionRequestNoteDraft(e.target.value)}
                placeholder="수정요청 사유(필수)"
                disabled={!!savingAction}
              />
            </div>

            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded bg-white border border-gray-200 text-gray-700 disabled:opacity-50"
                disabled={!!savingAction}
                onClick={() => closeContentRevisionRequestModal()}
              >
                취소
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded bg-red-600 text-white disabled:opacity-50"
                disabled={!!savingAction}
                onClick={() => void submitContentRevisionRequest()}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {decisionChoiceModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-md bg-white shadow-lg">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="text-sm font-semibold text-gray-900">계약 검토</div>
              <div className="text-xs text-gray-500 mt-1">진행할 작업을 선택하세요.</div>
            </div>

            <div className="px-4 py-4 space-y-2">
              {canDecide && (
                <button
                  type="button"
                  className="w-full rounded-lg border border-gray-200 px-4 py-3 text-left hover:bg-gray-50"
                  onClick={() => {
                    setDecisionChoiceModalOpen(false);
                    setError('');
                    setDecisionReasonDraft('');
                    setDecisionAction('APPROVE');
                    setDecisionModalOpen(true);
                  }}
                  disabled={!!savingAction || conditionSaving}
                >
                  <div className="text-sm font-semibold text-gray-900">승인</div>
                  <div className="mt-1 text-xs text-gray-500">계약을 승인하고 다음 단계로 진행합니다.</div>
                </button>
              )}
              {canDecide && (
                <button
                  type="button"
                  className="w-full rounded-lg border border-red-200 px-4 py-3 text-left hover:bg-red-50"
                  onClick={() => {
                    setDecisionChoiceModalOpen(false);
                    setError('');
                    setDecisionReasonDraft('');
                    setDecisionAction('REJECT');
                    setDecisionModalOpen(true);
                  }}
                  disabled={!!savingAction || conditionSaving}
                >
                  <div className="text-sm font-semibold text-red-700">반려</div>
                  <div className="mt-1 text-xs text-gray-500">반려 사유를 입력하고 계약을 반려합니다.</div>
                </button>
              )}
              {canProposeContractCondition && (
                <button
                  type="button"
                  className="w-full rounded-lg border border-indigo-200 px-4 py-3 text-left hover:bg-indigo-50"
                  onClick={() => {
                    setDecisionChoiceModalOpen(false);
                    setError('');
                    openConditionModal();
                  }}
                  disabled={!!savingAction || conditionSaving}
                >
                  <div className="text-sm font-semibold text-indigo-700">계약조건 재제안</div>
                  <div className="mt-1 text-xs text-gray-500">조건을 조정해서 다시 제안합니다.</div>
                </button>
              )}
            </div>

            <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50"
                disabled={!!savingAction || conditionSaving}
                onClick={() => setDecisionChoiceModalOpen(false)}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {decisionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-md bg-white shadow-lg">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="text-sm font-semibold text-gray-900">
                {String(decisionAction || '').toUpperCase() === 'APPROVE' ? '승인 처리' : '반려 처리'}
              </div>
              <div className="text-xs text-gray-500 mt-1">사유를 입력한 후 저장하세요.</div>
            </div>

            <div className="px-4 py-3 space-y-2">
              {error && <div className="text-sm text-red-600">{error}</div>}

              <textarea
                className="w-full border rounded px-3 py-2 text-sm"
                rows={5}
                value={decisionReasonDraft}
                onChange={(e) => setDecisionReasonDraft(e.target.value)}
                placeholder={String(decisionAction || '').toUpperCase() === 'APPROVE' ? '승인 사유(필수)' : '반려 사유(필수)'}
                disabled={!!savingAction}
              />
            </div>

            <div className="px-4 py-3 border-t border-gray-200 flex gap-2 justify-end">
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                disabled={!!savingAction}
                onClick={() => {
                  setDecisionModalOpen(false);
                  setDecisionAction('');
                  setDecisionReasonDraft('');
                }}
              >
                닫기
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={!!savingAction || String(decisionReasonDraft || '').trim().length === 0}
                onClick={() => void handleDecisionSubmit()}
              >
                {savingAction === String(decisionAction || '').toUpperCase() ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {conditionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-md bg-white shadow-lg">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900">계약조건 재제안</div>
              <button
                type="button"
                className="text-sm text-gray-500 hover:text-gray-700"
                disabled={conditionSaving}
                onClick={() => {
                  if (conditionSaving) return;
                  setConditionModalOpen(false);
                  setConditionError('');
                }}
              >
                닫기
              </button>
            </div>
            <div className="px-4 py-4 space-y-3">
              <div className="rounded-md border border-indigo-200 bg-indigo-50/40 px-3 py-3 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-indigo-900">계약 조건 수정 제안</div>
                    <div className="mt-1 text-[11px] text-gray-600 whitespace-pre-wrap break-words">
                      현재 계약조건을 기준으로 수정안을 입력해주세요. 현재값과 다른 항목만 제안으로 저장됩니다.
                    </div>
                  </div>
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold shrink-0 bg-indigo-100 text-indigo-800">
                    제안
                  </span>
                </div>

                <dl className="grid grid-cols-12 gap-x-2 text-xs">
                  <div className="col-span-12 grid grid-cols-12 items-center gap-x-2 py-1">
                    <dt className="col-span-4 text-gray-600" />
                    <dd className="col-span-4 text-gray-500 font-semibold text-center">현재</dd>
                    <dd className="col-span-4 text-indigo-900 font-semibold text-center">제안</dd>
                  </div>

                  <div className="col-span-12 border-t border-gray-200/70 grid grid-cols-12 items-center gap-x-2 py-2">
                    <dt className="col-span-4 text-gray-600">판매 가능 시작일</dt>
                    <dd className="col-span-4 text-center font-medium text-gray-900">
                      {String(selected?.requestedSalesCondition?.sellStartDate || '-').trim() || '-'}
                    </dd>
                    <dd className="col-span-4">
                      <input
                        type="date"
                        value={conditionSellStartDate}
                        onChange={(e) => setConditionSellStartDate(e.target.value)}
                        className="w-full rounded-md border border-indigo-200 px-2 py-1.5 text-xs text-gray-800 focus:border-indigo-400 focus:outline-none"
                        disabled={conditionSaving}
                      />
                    </dd>
                  </div>

                  <div className="col-span-12 border-t border-gray-200/70 grid grid-cols-12 items-center gap-x-2 py-2">
                    <dt className="col-span-4 text-gray-600">판매 가능 종료일</dt>
                    <dd className="col-span-4 text-center font-medium text-gray-900">
                      {String(selected?.requestedSalesCondition?.sellEndDate || '-').trim() || '-'}
                    </dd>
                    <dd className="col-span-4">
                      <input
                        type="date"
                        value={conditionSellEndDate}
                        onChange={(e) => setConditionSellEndDate(e.target.value)}
                        className="w-full rounded-md border border-indigo-200 px-2 py-1.5 text-xs text-gray-800 focus:border-indigo-400 focus:outline-none"
                        disabled={conditionSaving}
                      />
                    </dd>
                  </div>

                  <div className="col-span-12 border-t border-gray-200/70 grid grid-cols-12 items-center gap-x-2 py-2">
                    <dt className="col-span-4 text-gray-600">필수 작업물 제출 기한</dt>
                    <dd className="col-span-4 text-center font-medium text-gray-900">
                      {String(selected?.requestedSalesCondition?.deliverableSubmitDeadline || '-').trim() || '-'}
                    </dd>
                    <dd className="col-span-4">
                      <input
                        type="date"
                        value={conditionDeliverableSubmitDeadline}
                        onChange={(e) => setConditionDeliverableSubmitDeadline(e.target.value)}
                        className="w-full rounded-md border border-indigo-200 px-2 py-1.5 text-xs text-gray-800 focus:border-indigo-400 focus:outline-none"
                        disabled={conditionSaving}
                      />
                    </dd>
                  </div>

                  <div className="col-span-12 border-t border-gray-200/70 grid grid-cols-12 items-center gap-x-2 py-2">
                    <dt className="col-span-4 text-gray-600">판매 수수료 방식</dt>
                    <dd className="col-span-4 text-center font-medium text-gray-900">
                      {String(selected?.requestedSalesCondition?.commissionPolicy || '-').trim() || '-'}
                    </dd>
                    <dd className="col-span-4">
                      <input
                        type="text"
                        value={conditionCommissionPolicy}
                        onChange={(e) => setConditionCommissionPolicy(e.target.value)}
                        className="w-full rounded-md border border-indigo-200 px-2 py-1.5 text-xs text-gray-800 focus:border-indigo-400 focus:outline-none"
                        placeholder="예) 정률 8%"
                        disabled={conditionSaving}
                      />
                    </dd>
                  </div>

                  <div className="col-span-12 border-t border-gray-200/70 grid grid-cols-12 items-center gap-x-2 py-2">
                    <dt className="col-span-4 text-gray-600">콘텐츠 제작 지원</dt>
                    <dd className="col-span-4 text-center font-medium text-gray-900">
                      {String(selected?.requestedSalesCondition?.contentCreationSupport || '-').trim() || '-'}
                    </dd>
                    <dd className="col-span-4">
                      <input
                        type="text"
                        value={conditionContentCreationSupport}
                        onChange={(e) => setConditionContentCreationSupport(e.target.value)}
                        className="w-full rounded-md border border-indigo-200 px-2 py-1.5 text-xs text-gray-800 focus:border-indigo-400 focus:outline-none"
                        placeholder="예) 사진 10장 제공"
                        disabled={conditionSaving}
                      />
                    </dd>
                  </div>
                </dl>
              </div>

              <textarea
                value={conditionMessage}
                onChange={(e) => setConditionMessage(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-indigo-400 focus:outline-none"
                rows={3}
                placeholder="메모(선택)"
                disabled={conditionSaving}
              />

              {conditionError && <div className="text-xs text-red-600">{conditionError}</div>}

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="px-3 py-2 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  disabled={conditionSaving}
                  onClick={() => setConditionModalOpen(false)}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="px-3 py-2 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-gray-400"
                  disabled={conditionSaving}
                  onClick={() => void handleSubmitConditionProposal()}
                >
                  {conditionSaving ? '제안 중...' : '제안 보내기'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractDetailPage;
